"use client";

import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useEffect, useMemo, useRef, useState } from "react";
import { AudioEngine } from "../lib/audio";
import { correctedAudioOriginMs, planAudioAlignment } from "../lib/audio-alignment";
import {
  captureSessionSummary,
  createAddPassCaptureSession,
  createNewCaptureSession,
  transitionCaptureSession,
  type CaptureSession,
  type CaptureSessionStatus,
} from "../lib/capture-session";
import { diagnoseMicrophoneError } from "../lib/microphone";
import type { NoiseCalibration } from "../lib/noise";
import {
  GRID_BEATS,
  applyNoteDurationOverrides,
  NOTE_NAMES,
  buildChord,
  buildMidiFile,
  buildMultiTrackMidiFile,
  calculateTapTempo,
  buildSmoothTransitions,
  detectKeys,
  ensureMinimumNoteDurationByBar,
  generateAccompaniment,
  midiToNoteName,
  midiNoteKey,
  noteDetailProfile,
  quantizePitchFrames,
  quantizePitchFramesByBar,
  isUnresolvedMidiNote,
  resolvedMidiNotes,
  scalePitchClasses,
  sensitivityProfile,
  suggestChordProgression,
  type AccompanimentMode,
  type BarProcessingSetting,
  type GridValue,
  type HarmonyRoute,
  type KeyCandidate,
  type MidiNote,
  type MidiTrack,
  type MidiTimingMetadata,
  type PitchFrame,
  type TrackRole,
} from "../lib/music";
import { analyzeVoiceMapSample, applyVoiceMapToFrame, applyVoiceSustainAssist, buildVoiceMapProfile, recommendedSensitivityFromLiveLevel, voiceCaptureThreshold, voiceMapImpact, voiceMapMatches, type VoiceMapProfile } from "../lib/voice";
import { PitchRoll } from "./PitchRoll";
import { PianoPositionMode } from "./PianoPositionMode";
import { MobilePianoRollEditor } from "./MobilePianoRollEditor";
import { ViewportDiagnostics } from "./ViewportDiagnostics";
import { HowItWorksPanel } from "./HowItWorksPanel";
import { ConciergePanel } from "./ConciergePanel";
import { buildConciergeProposal, type AnalysisSettings, type ConciergeProposal, type GuidedIssue } from "../lib/concierge";
import { convertWithKeyAssist, keyConfidenceLabel, type ConversionMode, type KeyAssistStrength } from "../lib/key-assist";
import type { MelodyTapAnalysis, MelodyTapRawGesture } from "../lib/melody-tap";
import { buildRhythmGuidedNotes, DEFAULT_TAP_LATENCY_MS, type RhythmGuideGesture } from "../lib/rhythm-guided";
import { RhythmGuidedPad } from "./RhythmGuidedPad";
import { MelodyTapGuide } from "./MelodyTapGuide";
import { AddPassPanel } from "./AddPassPanel";
import {
  advanceBarSelection,
  buildPassTapEvidence,
  DEFAULT_DEVICE_TIMING_OFFSETS,
  createInputPass,
  preRollBarCount,
  createMusicalClock,
  mergeInputPassNotes,
  nextEmptyBar,
  passRangeTicks,
  regionHasNotes,
  suppressCountClickTransient,
  type BarRange as InputBarRange,
  type BarSelection,
  type InputPass,
  type InputPassDraft,
  type MusicalClock,
  type DeviceTimingOffsets,
} from "../lib/input-pass";
import { assignPitchEvidenceToSlots, buildRhythmSlotsFromMelodyTaps, retainRawPitchEvidence, rhythmSlotsToEditableNotes, type RhythmSlot } from "../lib/rhythm-slots";
import { analyzeLocalRhythmRange, type LocalRhythmRefineResult, type RhythmRefineStrength } from "../lib/rhythm-refine";
import { releaseNativeSpeakerOutput, restoreNativeSpeakerOutput, type AudioOutputRoute } from "../lib/native-audio-output";
import { countInNumberAtAudioTime, isAudioBoundaryArmed, type CountInSchedule } from "../lib/metronome";
import type { HarmonyGuideMode, LiveKeySource } from "../lib/live-key-guide";
import {
  playbackStartBeat,
  previousBarStart,
  nudgeEditableNotes,
  recordMidiEdit,
  redoMidiEdit,
  undoMidiEdit,
  type MidiEditMode,
} from "../lib/midi-editor";
import type { PreviewInstrument } from "../lib/preview-instrument";
import { DEFAULT_OUTPUT_VOLUME, parseStoredOutputVolume } from "../lib/output-volume";
import { openMidiFile, saveMidiFile } from "../lib/midi-file";
import { parseMidiFile, type ImportedMidiFile } from "../lib/midi-import";
import { buildMidiExportFileName } from "../lib/export-filename";
import { loadCurrentProject, saveCurrentProject, type ProjectStorageKind } from "../lib/project-persistence";
import {
  createTrackWithClip,
  overdubMonitorMidiTracks,
  playbackMidiTracks,
  trackToMidiTrack,
  type ProjectClip,
  type ProjectTrack,
  type RecordingTiming,
} from "../lib/multitrack-model";
import { createRecordingTiming, observeRecordingStart } from "../lib/transport";
import {
  addVoiceDrawFrame,
  beginVoiceDrawGesture,
  cancelVoiceDrawGesture,
  commitVoiceDrawGesture,
  emptyVoiceDrawState,
  readyVoiceDrawState,
  updateVoiceDrawGesture,
  voiceDrawMicFailure,
  voiceDrawShouldPausePlayback,
  type VoiceDrawPitchOptions,
  type VoiceDrawState,
} from "../lib/voice-draw";
import { PwaUpdateBanner } from "./PwaUpdateBanner";
import { AnchoredPopover } from "./AnchoredPopover";
import { BpmNumberInput } from "./BpmNumberInput";
import { MultiTrackArrange } from "./MultiTrackArrange";

type Stage = "idle" | "preparing" | "counting" | "recording" | "stopping" | "result";
type TaskMode = "capture" | "edit" | "piano" | "settings";
type CaptureSegmentationMode = "auto" | "rhythm-guided";
type RhythmEvidenceMode = "rhythm-refine" | "rhythm-first";
type BarRange = { start: number; count: number };
type CaptureAudioEvent = { label: string; performanceMs: number; audioTime: number | null };
type MidiImportSession = ImportedMidiFile & { fileName: string; selectedTrackIndex: number };
type ActivePassSession = {
  draft: InputPassDraft;
  clock: MusicalClock;
  beforeNotes: MidiNote[];
  beforeFrames: PitchFrame[];
  clickTimes: number[];
};
const COUNT_IN = [1, 2, 3, 4];
const KEYBOARD_NOTES = Array.from({ length: 37 }, (_, index) => 48 + index);
const BLACK = new Set([1, 3, 6, 8, 10]);
const ROLE_LABELS: Record<TrackRole, string> = { melody: "メロディ", bass: "ベース", strings: "ストリングス", synth: "シンセ" };
const ROLE_COLORS: Record<TrackRole, string> = { melody: "#22d86f", bass: "#50a7ff", strings: "#b780ff", synth: "#ff9a4d" };
const TASK_OPTIONS: { value: TaskMode; icon: string; label: string; description: string }[] = [
  { value: "capture", icon: "⌂", label: "HOME", description: "したいことを選ぶ" },
  { value: "edit", icon: "▦", label: "EDIT", description: "直す" },
  { value: "piano", icon: "▤", label: "LIVE PIANO", description: "鍵盤を見る" },
  { value: "settings", icon: "⚙", label: "SETTINGS", description: "設定" },
];
const VOICE_MAP_STORAGE_KEY = "p013.voice-map.v1";
const OUTPUT_VOLUME_STORAGE_KEY = "p013.output-volume.v1";
const MUTED_OUTPUT_OPERATIONS = new Set([
  "voice-map",
  "settings-four-beat",
  "capture",
  "bar-retake",
  "melody-tap",
  "tap-tempo",
  "recorded-voice",
  "edit-play",
  "raw-midi",
  "arrangement-play",
  "track-play",
  "screen-keyboard",
]);
const nextSequence = (ids: string[]) => Math.max(0, ...ids.map((id) => Number(id.match(/(\d+)$/)?.[1] ?? 0))) + 1;
const sleep = (milliseconds: number) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));
const isAbortError = (caught: unknown) => caught instanceof Error && caught.name === "AbortError";

function formatTime(seconds: number): string {
  const minutes = Math.floor(seconds / 60).toString().padStart(2, "0");
  const rest = Math.floor(seconds % 60).toString().padStart(2, "0");
  return `${minutes}:${rest}`;
}

function microphoneErrorMessage(caught: unknown, operation: string): string {
  const diagnostic = diagnoseMicrophoneError(caught, operation);
  console.error(`[P-013 microphone] ${diagnostic.debugMessage}`, caught);
  return diagnostic.userMessage;
}
function loadStoredVoiceMap(): VoiceMapProfile | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = window.localStorage.getItem(VOICE_MAP_STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as VoiceMapProfile;
    return parsed.schemaVersion === 1 && Array.isArray(parsed.samples) ? parsed : null;
  } catch {
    window.localStorage.removeItem(VOICE_MAP_STORAGE_KEY);
    return null;
  }
}

function loadStoredOutputVolume(): number {
  if (typeof window === "undefined") return DEFAULT_OUTPUT_VOLUME;
  return parseStoredOutputVolume(window.localStorage.getItem(OUTPUT_VOLUME_STORAGE_KEY));
}
export function MultitrackVocalMidiStudio() {
  const engineRef = useRef<AudioEngine | null>(null);
  const sampleTimerRef = useRef<number | null>(null);
  const playbackTimerRef = useRef<number | null>(null);
  const playbackFrameRef = useRef<number | null>(null);
  const voiceAudioRef = useRef<HTMLAudioElement | null>(null);
  const voiceMapCancelRef = useRef(false);
  const voiceMapSkipRef = useRef(false);
  const redoRangeRef = useRef<BarRange | null>(null);
  const framesBeforeRedoRef = useRef<PitchFrame[]>([]);
  const autoStopTimerRef = useRef<number | null>(null);
  const inputTestTimerRef = useRef<number | null>(null);
  const voiceDrawTimerRef = useRef<number | null>(null);
  const voiceDrawResetTimerRef = useRef<number | null>(null);
  const voiceDrawStartedAtRef = useRef(0);
  const voiceDrawPrepareIdRef = useRef(0);
  const voiceDrawLockedMidiRef = useRef<number | null>(null);
  const voiceDrawStateRef = useRef<VoiceDrawState>(emptyVoiceDrawState());
  const editModeRef = useRef<MidiEditMode>("select");
  const inputTestStartedAtRef = useRef(0);
  const voiceMapToneRef = useRef<number | null>(null);
  const inputTestPeakRef = useRef(0);
  const isStoppingRef = useRef(false);
  const captureStartAudioTimeRef = useRef(0);
  const captureArmedAudioTimeRef = useRef<number | null>(null);
  const lastVisualUpdateRef = useRef(0);
  const captureAudioEventsRef = useRef<CaptureAudioEvent[]>([]);
  const framesRef = useRef<PitchFrame[]>([]);
  const voiceBlobRef = useRef<Blob | null>(null);
  const rhythmPlaybackUrlRef = useRef<string | null>(null);
  const voiceUrlRef = useRef<string | null>(null);
  const tapTimesRef = useRef<number[]>([]);
  const heldKeysRef = useRef(new Set<string>());
  const heldVoicesRef = useRef(new Map<string, number[]>());
  const nextTrackIdRef = useRef(1);
  const nextClipIdRef = useRef(1);
  const lastRecordingTimingRef = useRef<RecordingTiming | null>(null);
  const keyAnalysisBucketRef = useRef(-1);
  const melodyTapCancelRef = useRef(false);
  const rhythmGesturesRef = useRef<RhythmGuideGesture[]>([]);
  const activeRhythmGestureRef = useRef<{ pointerId: number; startTime: number } | null>(null);
  const rhythmSequenceRef = useRef(1);
  const rhythmEvidenceOriginRef = useRef<number | null>(null);
  const activePassSessionRef = useRef<ActivePassSession | null>(null);
  const activePassDraftRef = useRef<InputPassDraft | null>(null);
  const refineTargetPassRef = useRef<InputPass | null>(null);
  const captureSessionRef = useRef<CaptureSession | null>(null);
  const captureSessionSequenceRef = useRef(1);
  const captureAbortRef = useRef<AbortController | null>(null);
  const countFrameRef = useRef<number | null>(null);
  const captureDetailsTriggerRef = useRef<HTMLButtonElement>(null);
  const captureReturnToResultRef = useRef(false);
  const [stage, setStage] = useState<Stage>("idle");
  const [captureWorkspaceActive, setCaptureWorkspaceActive] = useState(false);
  const [homeTool, setHomeTool] = useState<"pitch" | "tempo" | null>(null);
  const [captureDetailsOpen, setCaptureDetailsOpen] = useState(false);
  const [bpm, setBpm] = useState(100);
  const [tapCount, setTapCount] = useState(0);
  const [grid, setGrid] = useState<GridValue>("1/8");
  const [timeNudgeGrid, setTimeNudgeGrid] = useState<GridValue>("1/8");
  const [audioAlignmentMs, setAudioAlignmentMs] = useState(0);
  const [baseAudioOffsetMs, setBaseAudioOffsetMs] = useState(0);
  const [noteDetail, setNoteDetail] = useState(72);
  const [micSensitivity, setMicSensitivity] = useState(65);
  const [referenceMidi, setReferenceMidi] = useState(60);
  const [referenceBars, setReferenceBars] = useState(4);
  const [count, setCount] = useState<number | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [frames, setFrames] = useState<PitchFrame[]>([]);
  const [rawNotes, setRawNotes] = useState<MidiNote[]>([]);
  const [minimumNoteBeats, setMinimumNoteBeats] = useState(0.25);
  const [lengthAssist, setLengthAssist] = useState(true);
  const [noiseCalibration, setNoiseCalibration] = useState<NoiseCalibration | null>(null);
  const [isCalibrating, setIsCalibrating] = useState(false);
  const [voiceProfile, setVoiceProfile] = useState<VoiceMapProfile | null>(loadStoredVoiceMap);
  const [voiceMapAssist, setVoiceMapAssist] = useState(true);
  const [isVoiceMapping, setIsVoiceMapping] = useState(false);
  const [voiceMapPrompt, setVoiceMapPrompt] = useState("基準音を聞いて、同じ音を歌います");
  const [harmonyRoute, setHarmonyRoute] = useState<HarmonyRoute>("melody-fit");
  const [voiceMapBeat, setVoiceMapBeat] = useState(0);
  const [voiceMapPhaseBeats, setVoiceMapPhaseBeats] = useState(4);
  const [chordOverrides, setChordOverrides] = useState<Record<number, number>>({});
  const [keys, setKeys] = useState<KeyCandidate[]>([]);
  const [selectedKey, setSelectedKey] = useState<KeyCandidate | null>(null);
  const [livePianoKey, setLivePianoKey] = useState<KeyCandidate | null>(null);
  const [livePianoKeySource, setLivePianoKeySource] = useState<LiveKeySource>("none");
  const [livePianoKeyGuide, setLivePianoKeyGuide] = useState(false);
  const [livePianoHarmony, setLivePianoHarmony] = useState<HarmonyGuideMode>("off");
  const [scaleLock, setScaleLock] = useState(false);
  const [seventh, setSeventh] = useState(false);
  const [accompanimentMode, setAccompanimentMode] = useState<AccompanimentMode>("off");
  const [exportMix, setExportMix] = useState(false);
  const [tracks, setTracks] = useState<ProjectTrack[]>([]);
  const [clips, setClips] = useState<ProjectClip[]>([]);
  const [selectedTrackId, setSelectedTrackId] = useState<string | null>(null);
  const [selectedClipId, setSelectedClipId] = useState<string | null>(null);
  const [trackName, setTrackName] = useState("メロディ 1");
  const [trackRole, setTrackRole] = useState<TrackRole>("melody");
  const [overdubMonitoringEnabled, setOverdubMonitoringEnabled] = useState(true);
  const [voiceUrl, setVoiceUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playheadBeat, setPlayheadBeat] = useState<number | null>(null);
  const [editMode, setEditMode] = useState<MidiEditMode>("select");
  const [previewInstrument, setPreviewInstrument] = useState<PreviewInstrument>("piano");
  const [voiceDrawState, setVoiceDrawState] = useState<VoiceDrawState>(emptyVoiceDrawState);
  const [playbackLabel, setPlaybackLabel] = useState("");
  const [horizontalZoom, setHorizontalZoom] = useState(1);
  const [selectedBar, setSelectedBar] = useState<number | null>(null);
  const [selectedBarCount, setSelectedBarCount] = useState(1);
  const [barSettings, setBarSettings] = useState<Record<number, BarProcessingSetting>>({});
  const [bridgeChords, setBridgeChords] = useState(true);
  const [showAllChords, setShowAllChords] = useState(false);
  const [voiceMapCanSkip, setVoiceMapCanSkip] = useState(false);
  const [redoRange, setRedoRange] = useState<BarRange | null>(null);
  const [editNotice, setEditNotice] = useState("");
  const [taskMode, setTaskMode] = useState<TaskMode>("capture");
  const [navOpen, setNavOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);
  const [outputVolume, setOutputVolume] = useState(loadStoredOutputVolume);
  const [isAudioChecking, setIsAudioChecking] = useState(false);
  const [audioDebug, setAudioDebug] = useState<ReturnType<AudioEngine["getDebugInfo"]> | null>(null);
  const [exportNotice, setExportNotice] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [projectTitle, setProjectTitle] = useState("無題の曲");
  const [exportMemo, setExportMemo] = useState("");
  const [exportCounter, setExportCounter] = useState(1);
  const [projectLastModified, setProjectLastModified] = useState<string | null>(null);
  const [projectStorage, setProjectStorage] = useState<ProjectStorageKind>("indexeddb");
  const [projectSaveState, setProjectSaveState] = useState<"loading" | "saving" | "saved" | "error">("loading");
  const [projectHydrated, setProjectHydrated] = useState(false);
  const [projectTiming, setProjectTiming] = useState<MidiTimingMetadata>({ ppq: 480, timeSignature: { numerator: 4, denominator: 4 } });
  const [microphonePermission, setMicrophonePermission] = useState<PermissionState | "unavailable">("unavailable");
  const [captureAudioEvents, setCaptureAudioEvents] = useState<CaptureAudioEvent[]>([]);
  const [captureAudioRoute, setCaptureAudioRoute] = useState<AudioOutputRoute | null>(null);
  const [conversionMode, setConversionMode] = useState<ConversionMode>("standard");
  const [keyAssistStrength, setKeyAssistStrength] = useState<KeyAssistStrength>("normal");
  const [keyAssistWeight, setKeyAssistWeight] = useState(34);
  const [settingsView, setSettingsView] = useState<"guided" | "advanced">("guided");
  const [conciergeOpen, setConciergeOpen] = useState(false);
  const [conciergePhase, setConciergePhase] = useState<"before" | "after">("before");
  const [conciergeProposal, setConciergeProposal] = useState<ConciergeProposal | null>(null);
  const [conciergeBefore, setConciergeBefore] = useState<{ settings: AnalysisSettings; noteCount: number } | null>(null);
  const [melodyTapUndo, setMelodyTapUndo] = useState<AnalysisSettings | null>(null);
  const [captureSegmentationMode, setCaptureSegmentationMode] = useState<CaptureSegmentationMode>("auto");
  const [tapLatencyMs, setTapLatencyMs] = useState(DEFAULT_TAP_LATENCY_MS);
  const [rhythmGestures, setRhythmGestures] = useState<RhythmGuideGesture[]>([]);
  const [activeRhythmStart, setActiveRhythmStart] = useState<number | null>(null);
  const [rhythmSession, setRhythmSession] = useState<{ gestures: RhythmGuideGesture[]; captureDurationSeconds: number } | null>(null);
  const [rhythmEvidenceMode, setRhythmEvidenceMode] = useState<RhythmEvidenceMode | null>(null);
  const [rhythmSlots, setRhythmSlots] = useState<RhythmSlot[]>([]);
  const [rhythmPickupEnabled, setRhythmPickupEnabled] = useState(false);
  const [addPassOpen, setAddPassOpen] = useState(false);
  const [inputPasses, setInputPasses] = useState<InputPass[]>([]);
  const [activePassDraft, setActivePassDraft] = useState<InputPassDraft | null>(null);
  const [deviceTimingOffsets] = useState<DeviceTimingOffsets>(() => ({ ...DEFAULT_DEVICE_TIMING_OFFSETS }));
  const [rhythmRefineSelection, setRhythmRefineSelection] = useState<BarSelection | null>(null);
  const [rhythmRefineStrength, setRhythmRefineStrength] = useState<RhythmRefineStrength>("NORMAL");
  const [rhythmRefinePreview, setRhythmRefinePreview] = useState<LocalRhythmRefineResult | null>(null);
  const [captureSession, setCaptureSession] = useState<CaptureSession | null>(null);

  const [isInputTesting, setIsInputTesting] = useState(false);
  const [inputTestFrame, setInputTestFrame] = useState<PitchFrame | null>(null);
  const [inputTestPeak, setInputTestPeak] = useState(0);
  const [inputTestMessage, setInputTestMessage] = useState("録音せずに、今の音程と拾われ方を確認できます");
  const [manualDurationOverrides, setManualDurationOverrides] = useState<Record<string, number>>({});
  const [selectedNoteIndex, setSelectedNoteIndex] = useState<number | null>(null);
  const [editedNotes, setEditedNotes] = useState<MidiNote[] | null>(null);
  const [noteUndo, setNoteUndo] = useState<MidiNote[][]>([]);
  const [noteRedo, setNoteRedo] = useState<MidiNote[][]>([]);
  const [midiImport, setMidiImport] = useState<MidiImportSession | null>(null);
  const [editorMountKey, setEditorMountKey] = useState(0);
  const engine = () => {
    if (!engineRef.current) {
      engineRef.current = new AudioEngine();
      engineRef.current.setOutputVolume(outputVolume);
    }
    return engineRef.current;
  };

  function replaceVoiceSource(blob: Blob | null) {
    if (voiceUrlRef.current) URL.revokeObjectURL(voiceUrlRef.current);
    voiceBlobRef.current = blob;
    const nextUrl = blob ? URL.createObjectURL(blob) : null;
    voiceUrlRef.current = nextUrl;
    setVoiceUrl(nextUrl);
    if (blob) console.info("[P-013 audio] active voice source replaced", { mimeType: blob.type, size: blob.size });
  }

  function releaseRhythmPlaybackUrl() {
    if (!rhythmPlaybackUrlRef.current) return;
    URL.revokeObjectURL(rhythmPlaybackUrlRef.current);
    rhythmPlaybackUrlRef.current = null;
  }

  function selectedInputRange(): InputBarRange | null {
    return rhythmRefineSelection ? { startBar: rhythmRefineSelection.startBar, endBar: rhythmRefineSelection.endBar } : null;
  }

  function nextCaptureSessionId(): string {
    const id = `capture-${captureSessionSequenceRef.current}`;
    captureSessionSequenceRef.current += 1;
    return id;
  }

  function publishCaptureSession(session: CaptureSession) {
    captureSessionRef.current = session;
    setCaptureSession(session);
    if (process.env.NODE_ENV !== "production") console.debug("[P-013 capture session]", session);
  }

  function setCaptureSessionStatus(status: CaptureSessionStatus, patch: Partial<Omit<CaptureSession, "sessionId" | "status">> = {}) {
    const current = captureSessionRef.current;
    if (!current) return;
    if ((current.status === "COMPLETE" || current.status === "CANCELLED") && status !== current.status) return;
    publishCaptureSession(transitionCaptureSession(current, status, patch));
  }

  function clearPassCaptureState() {
    activePassSessionRef.current = null;
    activePassDraftRef.current = null;
    refineTargetPassRef.current = null;
    setActivePassDraft(null);
  }

  function beginCaptureRun(returnToResult = false): AbortSignal {
    captureAbortRef.current?.abort();
    const controller = new AbortController();
    captureAbortRef.current = controller;
    if (sampleTimerRef.current !== null) window.clearInterval(sampleTimerRef.current);
    if (autoStopTimerRef.current !== null) window.clearTimeout(autoStopTimerRef.current);
    if (countFrameRef.current !== null) window.cancelAnimationFrame(countFrameRef.current);
    sampleTimerRef.current = null;
    autoStopTimerRef.current = null;
    countFrameRef.current = null;
    captureReturnToResultRef.current = returnToResult;
    setCount(null);
    return controller.signal;
  }

  function cancelCapturePreparation() {
    captureAbortRef.current?.abort();
    captureAbortRef.current = null;
    if (countFrameRef.current !== null) window.cancelAnimationFrame(countFrameRef.current);
    countFrameRef.current = null;
    setCount(null);
    captureArmedAudioTimeRef.current = null;
    engineRef.current?.stopAllAudio();
    engineRef.current?.releaseMicrophone();
    resetRhythmCapture(true);
    setCaptureSessionStatus("CANCELLED");
    setStage(captureReturnToResultRef.current ? "result" : "idle");
    isStoppingRef.current = false;
  }

  async function ensureWorkspaceAudio(operation: string): Promise<AudioEngine | null> {
    if (outputVolume === 0 && MUTED_OUTPUT_OPERATIONS.has(operation)) {
      setError("アプリ音量が0です。CAPTUREのVOLスライダーですぐ変更できます。");
    }
    const audio = engine();
    try {
      await audio.ensureAudioReady();
      setAudioDebug(audio.getDebugInfo());
      return audio;
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : String(caught);
      console.error("[P-013 audio] " + operation, audio.getDebugInfo(), caught);
      setAudioDebug(audio.getDebugInfo());
      setError("音声出力を開始できませんでした。端末のメディア音量を確認してください。" + (message ? " (" + message + ")" : ""));
      return null;
    }
  }

  useEffect(() => {
    const warmAudio = () => {
      void engine().warmUp()
        .then(() => setAudioDebug(engineRef.current?.getDebugInfo() ?? null))
        .catch((caught) => console.warn("[P-013 audio] warm-up failed; next explicit audio action will retry", caught));
    };
    window.addEventListener("pointerdown", warmAudio, { capture: true, once: true });
    return () => window.removeEventListener("pointerdown", warmAudio, { capture: true });
    // The first render captures the persisted output volume; AudioEngine remains a single instance.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    return () => {
      if (sampleTimerRef.current !== null) window.clearInterval(sampleTimerRef.current);
      if (playbackTimerRef.current !== null) window.clearTimeout(playbackTimerRef.current);
      if (playbackFrameRef.current !== null) window.cancelAnimationFrame(playbackFrameRef.current);
      if (autoStopTimerRef.current !== null) window.clearTimeout(autoStopTimerRef.current);
      voiceAudioRef.current?.pause();
      if (voiceUrlRef.current) URL.revokeObjectURL(voiceUrlRef.current);
      void engineRef.current?.dispose();
      if (inputTestTimerRef.current !== null) window.clearInterval(inputTestTimerRef.current);
      releaseRhythmPlaybackUrl();
      if (voiceDrawTimerRef.current !== null) window.clearInterval(voiceDrawTimerRef.current);
      if (voiceDrawResetTimerRef.current !== null) window.clearTimeout(voiceDrawResetTimerRef.current);
      if (countFrameRef.current !== null) window.cancelAnimationFrame(countFrameRef.current);
      captureAbortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    let active = true;
    void loadCurrentProject().then(({ project, storage }) => {
      if (!active) return;
      setProjectStorage(storage);
      if (project) {
        const restoredNotes = project.notes.map((note) => ({ ...note }));
        setProjectTitle(project.title);
        setExportMemo(project.exportMemo);
        setExportCounter(project.exportCounter);
        setProjectLastModified(project.lastModified);
        setRawNotes(restoredNotes);
        setEditedNotes(restoredNotes);
        setTracks(project.tracks.map((track) => ({ ...track, clipIds: [...track.clipIds] })));
        setClips(project.clips.map((clip) => ({ ...clip, midi: clip.midi ? { notes: clip.midi.notes.map((note) => ({ ...note })) } : null })));
        nextTrackIdRef.current = nextSequence(project.tracks.map((track) => track.id));
        nextClipIdRef.current = nextSequence(project.clips.map((clip) => clip.id));
        setSelectedTrackId(project.tracks[0]?.id ?? null);
        setSelectedClipId(project.clips[0]?.id ?? null);
        setProjectTiming(project.timing);
        setBpm(project.settings.bpm);
        setGrid(project.settings.grid);
        setTimeNudgeGrid(project.settings.timeNudgeGrid);
        setMinimumNoteBeats(project.settings.minimumNoteBeats);
        setLengthAssist(project.settings.lengthAssist);
        setMicSensitivity(project.settings.micSensitivity);
        setNoteDetail(project.settings.noteDetail);
        setReferenceBars(project.settings.referenceBars);
        setBarSettings(project.settings.barSettings);
        if (restoredNotes.length) {
          const detected = detectKeys(restoredNotes);
          setKeys(detected);
          setSelectedKey(detected[0] ?? null);
          setStage("result");
          setCaptureWorkspaceActive(true);
        }
      }
      setProjectHydrated(true);
      setProjectSaveState("saved");
    }).catch((caught) => {
      console.error("[P-013 project] restore failed", caught);
      if (active) {
        setProjectHydrated(true);
        setProjectSaveState("error");
      }
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const permissions = navigator.permissions;
    if (!permissions?.query) return;
    let status: PermissionStatus | null = null;
    const update = () => setMicrophonePermission(status?.state ?? "unavailable");
    void permissions.query({ name: "microphone" } as PermissionDescriptor).then((nextStatus) => {
      status = nextStatus;
      update();
      status.addEventListener("change", update);
    }).catch(() => setMicrophonePermission("unavailable"));
    return () => status?.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    const stopForLifecycle = () => {
      engineRef.current?.stopAllAudio();
      voiceAudioRef.current?.pause();
      voiceAudioRef.current = null;
      if (playbackTimerRef.current !== null) window.clearTimeout(playbackTimerRef.current);
      if (playbackFrameRef.current !== null) window.cancelAnimationFrame(playbackFrameRef.current);
      playbackTimerRef.current = null;
      playbackFrameRef.current = null;
      setIsPlaying(false);
      setPlaybackLabel("");
    };
    const onVisibility = () => { if (document.hidden) stopForLifecycle(); };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("orientationchange", stopForLifecycle);
    window.addEventListener("pagehide", stopForLifecycle);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("orientationchange", stopForLifecycle);
      window.removeEventListener("pagehide", stopForLifecycle);
    };
  }, []);

  useEffect(() => {
    if (taskMode !== "settings") return;
    const update = () => setAudioDebug(engineRef.current?.getDebugInfo() ?? null);
    update();
    const timer = window.setInterval(update, 500);
    return () => window.clearInterval(timer);
  }, [taskMode]);
  useEffect(() => {
    if (!navOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setNavOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [navOpen]);

  const micProfile = useMemo(() => sensitivityProfile(micSensitivity), [micSensitivity]);
  const detailProfile = useMemo(() => noteDetailProfile(noteDetail), [noteDetail]);
  const activeTracks = useMemo(
    () => playbackMidiTracks(tracks, clips, projectTiming.ppq ?? 480),
    [clips, projectTiming.ppq, tracks],
  );
  const monitorMidiTracks = useMemo(
    () => overdubMonitorMidiTracks(tracks, clips, projectTiming.ppq ?? 480),
    [clips, projectTiming.ppq, tracks],
  );
  const exportMidiTracks = useMemo(
    () => tracks.map((track) => trackToMidiTrack(track, clips, projectTiming.ppq ?? 480)).filter((track) => track.notes.length),
    [clips, projectTiming.ppq, tracks],
  );
  const softToneMode = micSensitivity >= 75;
  const activeCalibration = noiseCalibration?.preserveSoftTone === softToneMode ? noiseCalibration : null;
  const activeVoiceProfile = voiceProfile && activeCalibration && voiceMapMatches(voiceProfile, activeCalibration.deviceLabel, softToneMode)
    ? voiceProfile
    : null;
  const manuallyEditedNoteKeys = useMemo(() => new Set(Object.keys(manualDurationOverrides)), [manualDurationOverrides]);
  const keyAssistResult = useMemo(
    () => convertWithKeyAssist(conversionMode, frames, rawNotes, bpm, keyAssistStrength, manuallyEditedNoteKeys, keyAssistWeight),
    [bpm, conversionMode, frames, keyAssistStrength, keyAssistWeight, manuallyEditedNoteKeys, rawNotes],
  );
  const currentKeySegment = keyAssistResult.segments.at(-1) ?? null;
  const minimumAssistedNotes = useMemo(
    () => ensureMinimumNoteDurationByBar(keyAssistResult.notes, lengthAssist ? minimumNoteBeats : null, barSettings),
    [barSettings, keyAssistResult.notes, lengthAssist, minimumNoteBeats],
  );
  const voiceAssistedNotes = useMemo(
    () => voiceMapAssist && activeVoiceProfile
      ? applyVoiceSustainAssist(minimumAssistedNotes, activeVoiceProfile, GRID_BEATS[grid])
      : minimumAssistedNotes,
    [activeVoiceProfile, grid, minimumAssistedNotes, voiceMapAssist],
  );
  const assistedNotes = useMemo(
    () => applyNoteDurationOverrides(voiceAssistedNotes, manualDurationOverrides),
    [manualDurationOverrides, voiceAssistedNotes],
  );
  const notes = editedNotes ?? assistedNotes;
  const resolvedNotes = useMemo(() => resolvedMidiNotes(notes), [notes]);
  const unresolvedNoteCount = notes.length - resolvedNotes.length;
  const currentMidiTiming: MidiTimingMetadata = projectTiming;
  const currentPpq = currentMidiTiming.ppq ?? 480;
  const currentTimeSignature = useMemo(
    () => currentMidiTiming.timeSignature ?? { numerator: 4, denominator: 4 },
    [currentMidiTiming.timeSignature],
  );
  const beatsPerBar = currentTimeSignature.numerator * 4 / currentTimeSignature.denominator;
  const nextPassBar = nextEmptyBar(notes, currentTimeSignature, currentPpq);
  const extendedNoteCount = useMemo(
    () => notes.filter((note, index) => note.durationBeats > (rawNotes[index]?.durationBeats ?? note.durationBeats) + 0.0001).length,
    [notes, rawNotes],
  );
  const recordedFrame = frames.at(-1);

  useEffect(() => {
    if (!projectHydrated) return;
    const timer = window.setTimeout(() => {
      setProjectSaveState("saving");
      const lastModified = new Date().toISOString();
      void saveCurrentProject({
        schemaVersion: 2,
        id: "current",
        title: projectTitle,
        exportMemo,
        exportCounter,
        lastModified,
        notes: notes.map((note) => ({ ...note })),
        tracks: tracks.map((track) => ({ ...track, clipIds: [...track.clipIds] })),
        clips: clips.map((clip) => ({ ...clip, midi: clip.midi ? { notes: clip.midi.notes.map((note) => ({ ...note })) } : null })),
        timing: {
          ppq: currentPpq,
          timeSignature: currentTimeSignature,
        },
        settings: {
          bpm,
          grid,
          timeNudgeGrid,
          minimumNoteBeats,
          lengthAssist,
          micSensitivity,
          noteDetail,
          referenceBars,
          barSettings,
        },
      }).then((storage) => {
        setProjectStorage(storage);
        setProjectLastModified(lastModified);
        setProjectSaveState("saved");
      }).catch((caught) => {
        console.error("[P-013 project] autosave failed", caught);
        setProjectSaveState("error");
      });
    }, 650);
    return () => window.clearTimeout(timer);
  }, [barSettings, bpm, clips, currentPpq, currentTimeSignature, exportCounter, exportMemo, grid, lengthAssist, micSensitivity, minimumNoteBeats, noteDetail, notes, projectHydrated, projectTitle, referenceBars, timeNudgeGrid, tracks]);
  const currentFrame = inputTestFrame ?? recordedFrame;
  const duration = Math.max(elapsed, frames.at(-1)?.time ?? 0);
  const scale = useMemo(
    () => selectedKey ? scalePitchClasses(selectedKey.tonicIndex, selectedKey.mode) : new Set<number>(),
    [selectedKey],
  );
  const suggestedChords = useMemo(
    () => suggestChordProgression(resolvedNotes, selectedKey, harmonyRoute),
    [harmonyRoute, resolvedNotes, selectedKey],
  );
  const chordPlan = useMemo(() => suggestedChords.map((suggestion) => {
    const override = chordOverrides[suggestion.bar];
    const selected = suggestion.candidates.find((candidate) => candidate.degree === override) ?? suggestion.candidates[0];
    return { ...suggestion, ...selected };
  }), [chordOverrides, suggestedChords]);
  const effectiveSeventh = seventh || harmonyRoute === "dramatic";
  const chordTransitions = useMemo(
    () => harmonyRoute === "smooth" && bridgeChords ? buildSmoothTransitions(chordPlan, selectedKey) : [],
    [bridgeChords, chordPlan, harmonyRoute, selectedKey],
  );
  const accompaniment = useMemo(
    () => generateAccompaniment(resolvedNotes, selectedKey, accompanimentMode, effectiveSeventh, chordPlan.map((suggestion) => suggestion.degree), chordTransitions),
    [resolvedNotes, selectedKey, accompanimentMode, effectiveSeventh, chordPlan, chordTransitions],
  );
  const timelineTracks = useMemo(() => tracks.map((track) => ({
    ...trackToMidiTrack(track, clips, currentPpq),
    id: track.id,
    enabled: !track.muted && (track.playbackMode === "midi" || track.playbackMode === "both"),
    color: ROLE_COLORS[track.role],
  })), [clips, currentPpq, tracks]);
  const totalBars = Math.max(1, Math.ceil(Math.max(duration * bpm / 60, ...notes.map((note) => note.startBeat + note.durationBeats), beatsPerBar) / beatsPerBar));
  const activeSelectedBarCount = selectedBar === null ? 1 : Math.max(1, Math.min(selectedBarCount, totalBars - selectedBar));
  const selectedBars = selectedBar === null ? [] : Array.from({ length: activeSelectedBarCount }, (_, index) => selectedBar + index);
  const selectedMidi = useMemo(
    () => exportMix && accompaniment.length ? [...resolvedNotes, ...accompaniment] : resolvedNotes,
    [resolvedNotes, accompaniment, exportMix],
  );

  useEffect(() => {
    if (stage !== "result" || !frames.length) return;
    const nextRawNotes = captureSegmentationMode === "rhythm-guided" && rhythmSession
      ? buildRhythmGuidedNotes(frames, rhythmSession.gestures, {
        bpm,
        grid,
        latencyMs: tapLatencyMs,
        confidenceThreshold: micProfile.confidenceThreshold,
        captureDurationSeconds: rhythmSession.captureDurationSeconds,
      }).notes
      : quantizePitchFramesByBar(frames, bpm, grid, barSettings, micProfile.voicedThreshold, micProfile.confidenceThreshold, noteDetail);
    const pitchNotes = convertWithKeyAssist(conversionMode, frames, nextRawNotes, bpm, keyAssistStrength, manuallyEditedNoteKeys, keyAssistWeight).notes;
    const minimumNotes = ensureMinimumNoteDurationByBar(pitchNotes, lengthAssist ? minimumNoteBeats : null, barSettings);
    const nextNotes = voiceMapAssist && activeVoiceProfile ? applyVoiceSustainAssist(minimumNotes, activeVoiceProfile, GRID_BEATS[grid]) : minimumNotes;
    const timer = window.setTimeout(() => {
      setRawNotes(nextRawNotes);
      setKeys(detectKeys(nextNotes));
    }, 0);
    return () => window.clearTimeout(timer);
  }, [activeVoiceProfile, barSettings, bpm, captureSegmentationMode, conversionMode, frames, grid, keyAssistStrength, keyAssistWeight, lengthAssist, manuallyEditedNoteKeys, micProfile.confidenceThreshold, micProfile.voicedThreshold, minimumNoteBeats, noteDetail, rhythmSession, stage, tapLatencyMs, voiceMapAssist]);

  function clearPlaybackTimers() {
    if (playbackTimerRef.current !== null) window.clearTimeout(playbackTimerRef.current);
    if (playbackFrameRef.current !== null) window.cancelAnimationFrame(playbackFrameRef.current);
    playbackTimerRef.current = null;
    playbackFrameRef.current = null;
  }

  function clearPlaybackState() {
    clearPlaybackTimers();
    setIsPlaying(false);
    setPlayheadBeat(null);
    setPlaybackLabel("");
  }

  function stopPlayback() {
    engineRef.current?.stopAllAudio();
    if (voiceAudioRef.current) {
      voiceAudioRef.current.pause();
      voiceAudioRef.current.currentTime = 0;
      voiceAudioRef.current = null;
    }
    clearPlaybackState();
  }

  function haltEditorPlayback() {
    engineRef.current?.stopAllAudio();
    if (voiceAudioRef.current) {
      voiceAudioRef.current.pause();
      voiceAudioRef.current = null;
    }
    clearPlaybackTimers();
    setIsPlaying(false);
    setPlaybackLabel("");
  }

  function beginPlaybackProgress(milliseconds: number, label: string, startBeat = 0) {
    if (milliseconds <= 0) return clearPlaybackState();
    const started = performance.now(); // eslint-disable-line react-hooks/purity
    setIsPlaying(true);
    setPlaybackLabel(label);
    setPlayheadBeat(startBeat);
    const update = () => {
      const elapsedMs = performance.now() - started;
      setPlayheadBeat(startBeat + elapsedMs / 1000 * bpm / 60);
      if (elapsedMs >= milliseconds) return clearPlaybackState();
      playbackFrameRef.current = window.requestAnimationFrame(update);
    };
    playbackFrameRef.current = window.requestAnimationFrame(update);
  }

  function beginEditorPlaybackProgress(milliseconds: number, label: string, startBeat: number) {
    if (milliseconds <= 0) {
      haltEditorPlayback();
      return;
    }
    const started = performance.now(); // eslint-disable-line react-hooks/purity
    const endBeat = startBeat + milliseconds / 1000 * bpm / 60;
    setIsPlaying(true);
    setPlaybackLabel(label);
    setPlayheadBeat(startBeat);
    const update = () => {
      const elapsedMs = performance.now() - started;
      setPlayheadBeat(Math.min(endBeat, startBeat + elapsedMs / 1000 * bpm / 60));
      if (elapsedMs >= milliseconds) {
        engineRef.current?.stopPlayback();
        clearPlaybackTimers();
        setIsPlaying(false);
        setPlaybackLabel("");
        return;
      }
      playbackFrameRef.current = window.requestAnimationFrame(update);
    };
    playbackFrameRef.current = window.requestAnimationFrame(update);
  }

  async function calibrateEnvironment() {
    setError(null);
    setIsCalibrating(true);
    const audio = await ensureWorkspaceAudio("room-zero");
    if (!audio) { setIsCalibrating(false); return; }
    try {
      await audio.prepareMicrophone(softToneMode);
      const calibration = await audio.calibrateSilence(3000);
      setNoiseCalibration(calibration);
    } catch (caught) {
      setError(microphoneErrorMessage(caught, "room-zero"));
    } finally {
      audio.releaseMicrophone();
      setIsCalibrating(false);
    }
  }

  async function collectVoiceMapFrames(audio: AudioEngine, durationMs: number, beats: number): Promise<PitchFrame[]> {
    const sampled: PitchFrame[] = [];
    const started = performance.now(); // eslint-disable-line react-hooks/purity
    let displayedBeat = 0;
    while (performance.now() - started < durationMs && !voiceMapCancelRef.current && !voiceMapSkipRef.current) { // eslint-disable-line react-hooks/purity
      const elapsedMs = performance.now() - started; // eslint-disable-line react-hooks/purity
      const beat = Math.min(beats, Math.floor(elapsedMs / (60_000 / bpm)) + 1);
      if (beat !== displayedBeat) { displayedBeat = beat; setVoiceMapBeat(beat); }
      const time = elapsedMs / 1000;
      sampled.push(audio.samplePitch(time, 0.0035, activeCalibration?.p90 ?? 0));
      await sleep(55);
    }
    return sampled;
  }
  async function runVoiceMapCountIn(audio: AudioEngine) {
    const beatMs = 60_000 / bpm;
    setVoiceMapCanSkip(false);
    setVoiceMapPhaseBeats(4);
    for (const beat of COUNT_IN) {
      if (voiceMapCancelRef.current) return;
      setVoiceMapBeat(beat);
      setVoiceMapPrompt(`COUNT IN · ${beat} / 4`);
      await audio.playClick(beat === 1, selectedKey?.tonicIndex ?? null);
      await sleep(beatMs);
    }
    setVoiceMapBeat(0);
  }

  async function playVoiceMapReference(audio: AudioEngine, midi: number, beats: number): Promise<void> {
    const durationMs = beats * 60_000 / bpm;
    const started = performance.now(); // eslint-disable-line react-hooks/purity
    setVoiceMapPhaseBeats(beats);
    setVoiceMapBeat(1);
    const voiceId = await audio.startSustainedTone(midi, 0.12);
    voiceMapToneRef.current = voiceId;
    while (performance.now() - started < durationMs && !voiceMapCancelRef.current && !voiceMapSkipRef.current) { // eslint-disable-line react-hooks/purity
      const elapsedMs = performance.now() - started; // eslint-disable-line react-hooks/purity
      const beat = Math.min(beats, Math.floor(elapsedMs / (60_000 / bpm)) + 1);
      setVoiceMapBeat(beat);
      await sleep(35);
    }
    audio.stopSustainedTone(voiceId);
    if (voiceMapToneRef.current === voiceId) voiceMapToneRef.current = null;
    setVoiceMapBeat(0);
  }


  function skipVoiceMapNote() {
    voiceMapSkipRef.current = true;
    if (voiceMapToneRef.current !== null) engineRef.current?.stopSustainedTone(voiceMapToneRef.current);
    voiceMapToneRef.current = null;
    engineRef.current?.stopPlayback();
    setVoiceMapPrompt("この音は測定対象外としてスキップします");
    setVoiceMapBeat(0);
  }

  function stopVoiceMap() {
    voiceMapCancelRef.current = true;
    voiceMapSkipRef.current = true;
    if (voiceMapToneRef.current !== null) engineRef.current?.stopSustainedTone(voiceMapToneRef.current);
    voiceMapToneRef.current = null;
    engineRef.current?.stopGuide();
    engineRef.current?.stopPlayback();
    engineRef.current?.releaseMicrophone();
    setVoiceMapPrompt("VOICE MAPを途中で停止しました");
    setVoiceMapCanSkip(false);
    setVoiceMapBeat(0);
  }

  async function calibrateVoiceMap() {
    if (!activeCalibration) {
      setError("先に、実際に使うマイクでROOM ZEROを測ってください。");
      return;
    }
    setError(null);
    setIsVoiceMapping(true);
    voiceMapCancelRef.current = false;
    const audio = await ensureWorkspaceAudio("voice-map");
    if (!audio) { setIsVoiceMapping(false); return; }
    try {
      await audio.prepareMicrophone(softToneMode);
      await runVoiceMapCountIn(audio);
      if (voiceMapCancelRef.current) return;
      audio.startGuide(bpm, referenceMidi, 0, selectedKey?.tonicIndex ?? null);
      const input = audio.getInputInfo();
      const centerMidi = Math.max(48, Math.min(84, referenceMidi));
      const targets = [...new Set([-12, -5, 0, 5, 12].map((offset) => Math.max(36, Math.min(96, centerMidi + offset))))];
      const samples = [];
      const phraseMs = Math.round(4 * 60_000 / bpm);
      const phraseSeconds = phraseMs / 1000;
      for (let index = 0; index < targets.length; index += 1) {
        if (voiceMapCancelRef.current) break;
        const target = targets[index];
        voiceMapSkipRef.current = false;
        setVoiceMapCanSkip(true);
        setVoiceMapPrompt(`${index + 1}/${targets.length + 1} 4拍聴く · ${midiToNoteName(target)}`);
        await playVoiceMapReference(audio, target, 4);
        if (voiceMapCancelRef.current) break;
        if (voiceMapSkipRef.current) continue;
        setVoiceMapPrompt(`${index + 1}/${targets.length + 1} 4拍歌う · ${midiToNoteName(target)}`);
        const sampled = await collectVoiceMapFrames(audio, phraseMs, 4);
        if (!voiceMapSkipRef.current && !voiceMapCancelRef.current) samples.push(analyzeVoiceMapSample(target, sampled, phraseSeconds));
      }
      if (voiceMapCancelRef.current) return;
      if (!samples.length) throw new Error("No usable voice samples");
      voiceMapSkipRef.current = false;
      setVoiceMapPrompt(`${targets.length + 1}/${targets.length + 1} 8拍聴く · ロング ${midiToNoteName(centerMidi)}`);
      await playVoiceMapReference(audio, centerMidi, 8);
      if (voiceMapCancelRef.current) return;
      setVoiceMapPrompt(`${targets.length + 1}/${targets.length + 1} 8拍歌う · 同じ長さで止める`);
      const longPhraseMs = Math.round(8 * 60_000 / bpm);
      const longFrames = await collectVoiceMapFrames(audio, longPhraseMs, 8);
      const longSample = voiceMapSkipRef.current ? (samples.find((sample) => sample.targetMidi === centerMidi) ?? samples[0]) : analyzeVoiceMapSample(centerMidi, longFrames, longPhraseMs / 1000);
      const profile = buildVoiceMapProfile(samples, longSample, {
        deviceLabel: input.deviceLabel,
        preserveSoftTone: softToneMode,
        noiseP90: activeCalibration.p90,
        centerMidi,
      });
      setVoiceProfile(profile);
      setVoiceMapAssist(true);
      window.localStorage.setItem(VOICE_MAP_STORAGE_KEY, JSON.stringify(profile));
      setVoiceMapPrompt("VOICE MAPを端末に保存しました");
    } catch (caught) {
      if (!voiceMapCancelRef.current) {
        setError(microphoneErrorMessage(caught, "voice-map"));
        setVoiceMapPrompt("基準音を聞いて、同じ音を歌います");
      }
    } finally {
      audio.stopGuide();
      audio.stopPlayback();
      if (voiceMapToneRef.current !== null) audio.stopSustainedTone(voiceMapToneRef.current);
      voiceMapToneRef.current = null;
      audio.releaseMicrophone();
      setVoiceMapCanSkip(false);
      setVoiceMapBeat(0);
      setIsVoiceMapping(false);
    }
  }
  function clearVoiceMap() {
    setVoiceProfile(null);
    window.localStorage.removeItem(VOICE_MAP_STORAGE_KEY);
    setVoiceMapPrompt("基準音を聞いて、同じ音を歌います");
  }
  function sampledFrame(audio: AudioEngine, time: number): PitchFrame {
    const captureThreshold = activeVoiceProfile ? voiceCaptureThreshold(micProfile.rmsThreshold, activeVoiceProfile) : micProfile.rmsThreshold;
    const captured = audio.samplePitch(time, captureThreshold, activeCalibration?.p90 ?? 0);
    const adjusted = activeVoiceProfile ? applyVoiceMapToFrame(captured, activeVoiceProfile, micProfile.confidenceThreshold) : captured;
    const nearestMidi = adjusted.midiFloat === null ? null : Math.round(adjusted.midiFloat);
    return {
      ...adjusted,
      cents: nearestMidi === null ? null : Math.round(((adjusted.midiFloat as number) - nearestMidi) * 100),
      voiced: adjusted.frequency !== null && adjusted.midiFloat !== null && adjusted.confidence >= (adjusted.acceptanceConfidence ?? micProfile.confidenceThreshold),
    };
  }
  function publishVoiceDrawState(next: VoiceDrawState) {
    voiceDrawStateRef.current = next;
    setVoiceDrawState(next);
  }

  function currentVoiceDrawPitchOptions(): VoiceDrawPitchOptions {
    return {
      confidenceThreshold: micProfile.confidenceThreshold,
      minimumLevel: Math.max(0.0035, Math.min(0.008, micProfile.rmsThreshold)),
      minimumFrames: 3,
      minimumStableMs: 90,
      minimumWinnerShare: 0.62,
    };
  }

  function stopVoiceDrawSampling() {
    if (voiceDrawTimerRef.current !== null) window.clearInterval(voiceDrawTimerRef.current);
    voiceDrawTimerRef.current = null;
    voiceDrawLockedMidiRef.current = null;
  }

  function scheduleVoiceDrawReady(delay = 420) {
    if (voiceDrawResetTimerRef.current !== null) window.clearTimeout(voiceDrawResetTimerRef.current);
    voiceDrawResetTimerRef.current = window.setTimeout(() => {
      voiceDrawResetTimerRef.current = null;
      if (editModeRef.current === "voice") publishVoiceDrawState(readyVoiceDrawState());
    }, delay);
  }

  async function prepareVoiceDrawMode() {
    voiceDrawPrepareIdRef.current += 1;
    const prepareId = voiceDrawPrepareIdRef.current;
    if (isInputTesting) stopInputTest();
    if (isPlaying) haltEditorPlayback();
    editModeRef.current = "voice";
    setEditMode("voice");
    setSelectedNoteIndex(null);
    setError(null);
    publishVoiceDrawState(emptyVoiceDrawState("idle", "MIC PREPARING"));
    try {
      const audio = await ensureWorkspaceAudio("voice-draw");
      if (!audio) {
        publishVoiceDrawState(voiceDrawMicFailure("マイクを準備できませんでした"));
        return;
      }
      await audio.prepareMicrophone(softToneMode);
      if (prepareId !== voiceDrawPrepareIdRef.current || editModeRef.current !== "voice") {
        audio.releaseMicrophone();
        return;
      }
      publishVoiceDrawState(readyVoiceDrawState());
      setEditNotice("VOICE READY · 指で押している間だけPitchを読み取ります。");
    } catch (caught) {
      engineRef.current?.releaseMicrophone();
      if (prepareId !== voiceDrawPrepareIdRef.current) return;
      publishVoiceDrawState(voiceDrawMicFailure("マイクを開始できませんでした"));
      setError(microphoneErrorMessage(caught, "voice-draw"));
    }
  }

  function leaveVoiceDrawMode(nextMode: Exclude<MidiEditMode, "voice">) {
    voiceDrawPrepareIdRef.current += 1;
    stopVoiceDrawSampling();
    if (voiceDrawResetTimerRef.current !== null) window.clearTimeout(voiceDrawResetTimerRef.current);
    voiceDrawResetTimerRef.current = null;
    engineRef.current?.releaseMicrophone();
    editModeRef.current = nextMode;
    setEditMode(nextMode);
    setSelectedNoteIndex(null);
    publishVoiceDrawState(emptyVoiceDrawState());
  }

  function changeEditorMode(mode: MidiEditMode) {
    haltEditorPlayback();
    if (mode === "voice") {
      void prepareVoiceDrawMode();
      return;
    }
    if (isPlaying) haltEditorPlayback();
    if (editModeRef.current === "voice") leaveVoiceDrawMode(mode);
    else {
      editModeRef.current = mode;
      setEditMode(mode);
      setSelectedNoteIndex(null);
    }
  }

  function startVoiceDrawGesture(pointerId: number, anchorBeat: number, targetNoteIndex: number | null, totalBeats: number) {
    const current = voiceDrawStateRef.current;
    if (editModeRef.current !== "voice" || current.phase !== "ready") return;
    if (voiceDrawShouldPausePlayback(isPlaying, current)) haltEditorPlayback();
    else engineRef.current?.stopPlayback();
    const next = beginVoiceDrawGesture(current, pointerId, anchorBeat, targetNoteIndex, GRID_BEATS[grid], totalBeats);
    if (next === current) return;
    stopVoiceDrawSampling();
    voiceDrawStartedAtRef.current = performance.now(); // eslint-disable-line react-hooks/purity
    publishVoiceDrawState(next);
    voiceDrawTimerRef.current = window.setInterval(() => {
      try {
        const active = voiceDrawStateRef.current;
        if (active.pointerId === null) return;
        const audio = engineRef.current;
        if (!audio) throw new Error("Microphone is not ready");
        const time = (performance.now() - voiceDrawStartedAtRef.current) / 1000;
        const frame = sampledFrame(audio, time);
        const sampled = addVoiceDrawFrame(active, frame, currentVoiceDrawPitchOptions());
        if (sampled.lockedMidi !== null && sampled.lockedMidi !== voiceDrawLockedMidiRef.current) {
          voiceDrawLockedMidiRef.current = sampled.lockedMidi;
          if (typeof navigator.vibrate === "function") navigator.vibrate(18);
        }
        publishVoiceDrawState(sampled);
      } catch (caught) {
        stopVoiceDrawSampling();
        publishVoiceDrawState(voiceDrawMicFailure("マイク入力を継続できませんでした"));
        setError(microphoneErrorMessage(caught, "voice-draw"));
      }
    }, 55);
  }

  function moveVoiceDrawGesture(pointerId: number, currentBeat: number, totalBeats: number) {
    const current = voiceDrawStateRef.current;
    const next = updateVoiceDrawGesture(current, pointerId, currentBeat, GRID_BEATS[grid], totalBeats);
    if (next !== current) publishVoiceDrawState(next);
  }

  function finishVoiceDrawGesture(pointerId: number, currentBeat: number, totalBeats: number) {
    const ranged = updateVoiceDrawGesture(voiceDrawStateRef.current, pointerId, currentBeat, GRID_BEATS[grid], totalBeats);
    if (ranged.pointerId !== pointerId) return;
    publishVoiceDrawState(ranged);
    stopVoiceDrawSampling();
    const result = commitVoiceDrawGesture(ranged, notes, currentVoiceDrawPitchOptions());
    publishVoiceDrawState(result.state);
    setSelectedNoteIndex(result.selectedNoteIndex);
    if (result.committed) {
      commitEditorNotes(result.before, result.notes, result.message);
    } else {
      setEditNotice(result.message);
    }
    scheduleVoiceDrawReady(result.committed ? 280 : 720);
  }

  function cancelActiveVoiceDraw(pointerId: number | null, reason: string) {
    const current = voiceDrawStateRef.current;
    if (pointerId !== null && current.pointerId !== pointerId) return;
    if (current.pointerId === null) return;
    stopVoiceDrawSampling();
    publishVoiceDrawState(cancelVoiceDrawGesture(current, reason));
    setEditNotice(reason);
    scheduleVoiceDrawReady(620);
  }

  function stopInputTest() {
    if (inputTestTimerRef.current !== null) window.clearInterval(inputTestTimerRef.current);
    inputTestTimerRef.current = null;
    engineRef.current?.releaseMicrophone();
    setIsInputTesting(false);
  }

  async function startInputTest() {
    setError(null);
    setInputTestMessage("マイクを準備中…");
    setInputTestPeak(0);
    inputTestPeakRef.current = 0;
    setIsInputTesting(true);
    try {
      const audio = await ensureWorkspaceAudio("live-input-test");
      if (!audio) { setIsInputTesting(false); return; }
      await audio.prepareMicrophone(softToneMode);
      inputTestStartedAtRef.current = performance.now(); // eslint-disable-line react-hooks/purity
      inputTestTimerRef.current = window.setInterval(() => {
        const time = (performance.now() - inputTestStartedAtRef.current) / 1000;
        const frame = sampledFrame(audio, time);
        const level = frame.level ?? 0;
        inputTestPeakRef.current = Math.max(inputTestPeakRef.current, level);
        setInputTestPeak(inputTestPeakRef.current);
        setInputTestFrame(frame);
        const accepted = frame.midiFloat !== null && frame.confidence >= (frame.acceptanceConfidence ?? micProfile.confidenceThreshold);
        if (accepted) {
          setInputTestMessage(`認識中 · ${midiToNoteName(frame.midiFloat as number)} · confidence ${Math.round(frame.confidence * 100)}%`);
        } else if (level >= 0.0035) {
          setInputTestMessage("声量は届いていますが音程を確定できません。この声から調整するか、少し母音を明るく伸ばしてください。");
        } else {
          setInputTestMessage("現在の声量は拾音閾値より小さめです。普段の鼻歌を出したまま「この声から調整」を押せます。");
        }
      }, 55);
    } catch (caught) {
      engineRef.current?.releaseMicrophone();
      setIsInputTesting(false);
      setInputTestMessage("マイクを開始できませんでした");
      setError(microphoneErrorMessage(caught, "live-input-test"));
    }
  }

  function applyInputTestSensitivity() {
    const level = Math.max(inputTestPeakRef.current, inputTestPeak);
    const captureFactor = activeVoiceProfile
      ? voiceCaptureThreshold(0.012, activeVoiceProfile) / 0.012
      : 1;
    const suggested = recommendedSensitivityFromLiveLevel(level, captureFactor);
    const applied = Math.max(micSensitivity, suggested);
    const processingModeChanges = (applied >= 75) !== softToneMode;
    setMicSensitivity(applied);
    setInputTestMessage(`MIC PICKUPを ${applied} にしました。${processingModeChanges ? " 音声処理モードが変わるためROOM ZEROとVOICE MAPを取り直してください。" : " もう一度入力テストで確認できます。"}`);
    stopInputTest();
  }

  function applyVoiceMapRecommendedSensitivity() {
    if (!voiceProfile) return;
    const processingModeChanges = (voiceProfile.recommendedSensitivity >= 75) !== softToneMode;
    setMicSensitivity(voiceProfile.recommendedSensitivity);
    setVoiceMapPrompt(`推奨MIC PICKUP ${voiceProfile.recommendedSensitivity} を適用しました${processingModeChanges ? " · ROOM ZEROから取り直してください" : ""}`);
  }


  function markCaptureAudioEvent(label: string, audio?: AudioEngine, expectedAudioTime?: number) {
    const event = { label, performanceMs: performance.now(), audioTime: expectedAudioTime ?? audio?.getCurrentTime() ?? null }; // eslint-disable-line react-hooks/purity
    captureAudioEventsRef.current = [...captureAudioEventsRef.current, event].slice(-16);
    setCaptureAudioEvents(captureAudioEventsRef.current);
    if (process.env.NODE_ENV !== "production") console.debug(`[P-013 capture audio] ${label}`, event);
  }

  async function runCountIn(audio: AudioEngine, options: {
    onScheduled?: (schedule: CountInSchedule) => void;
    armRhythmInput?: boolean;
    inputDuringCount?: boolean;
    beats?: number;
    emitClicks?: boolean;
    sessionStatus?: "COUNT_IN" | "PRE_ROLL";
    signal?: AbortSignal;
  } = {}): Promise<CountInSchedule> {
    setStage("counting");
    setCaptureSessionStatus(options.sessionStatus ?? "COUNT_IN");
    const countBeats = Math.max(1, Math.round(options.beats ?? COUNT_IN.length));
    const schedule = await audio.scheduleCountIn(bpm, selectedKey?.tonicIndex ?? null, countBeats, options.emitClicks ?? true);
    if (options.armRhythmInput) captureArmedAudioTimeRef.current = options.inputDuringCount ? schedule.startTime : schedule.recordStartTime;
    markCaptureAudioEvent("Count scheduled", audio, schedule.startTime);
    schedule.beatTimes.forEach((time, index) => markCaptureAudioEvent("Beat" + (index + 1) + " expected", audio, time));
    options.onScheduled?.(schedule);
    let displayedCount = -1;
    const updateCountDisplay = () => {
      if (options.signal?.aborted) return;
      const nextCount = countInNumberAtAudioTime(schedule, audio.getCurrentTime());
      if (nextCount === null) {
        countFrameRef.current = null;
        return;
      }
      if (nextCount !== displayedCount) {
        displayedCount = nextCount;
        setCount(nextCount);
      }
      countFrameRef.current = window.requestAnimationFrame(updateCountDisplay);
    };
    updateCountDisplay();
    try {
      await audio.waitUntilAudioTime(schedule.recordStartTime, { signal: options.signal });
    } finally {
      if (countFrameRef.current !== null) window.cancelAnimationFrame(countFrameRef.current);
      countFrameRef.current = null;
      setCount(null);
    }
    return schedule;
  }

  async function restoreCaptureSpeakerOutput(): Promise<AudioOutputRoute | null> {
    try {
      const route = await restoreNativeSpeakerOutput();
      if (route) {
        setCaptureAudioRoute(route);
        console.info("[P-013 audio] capture speaker route", route);
      }
      return route;
    } catch (caught) {
      console.warn("[P-013 audio] native speaker route unavailable", caught);
      return null;
    }
  }
  async function releaseCaptureSpeakerOutput() {
    try {
      await releaseNativeSpeakerOutput();
    } catch (caught) {
      console.warn("[P-013 audio] native speaker route release unavailable", caught);
    }
  }
  function changeOutputVolume(next: number) {
    const value = Math.max(0, Math.min(100, Math.round(next)));
    setOutputVolume(value);
    window.localStorage.setItem(OUTPUT_VOLUME_STORAGE_KEY, String(value));
    const audio = engine();
    audio.setOutputVolume(value);
    setAudioDebug(audio.getDebugInfo());
    if (value > 0 && error?.startsWith("アプリ音量が0です")) setError(null);
  }
  async function playFourBeatCheck() {
    setIsAudioChecking(true);
    setError(null);
    const audio = await ensureWorkspaceAudio("settings-four-beat");
    if (!audio) { setIsAudioChecking(false); return; }
    try {
      const schedule = await audio.scheduleCountIn(bpm, selectedKey?.tonicIndex ?? null, COUNT_IN.length);
      await audio.waitUntilAudioTime(schedule.recordStartTime);
      setAudioDebug(audio.getDebugInfo());
    } finally {
      setIsAudioChecking(false);
    }
  }

  function resetRhythmCapture(clearStoredSession = true) {
    rhythmGesturesRef.current = [];
    activeRhythmGestureRef.current = null;
    rhythmSequenceRef.current = 1;
    setRhythmGestures([]);
    setActiveRhythmStart(null);
    if (clearStoredSession) setRhythmSession(null);
  }

  function startRhythmGesture(pointerId: number): boolean {
    const audioTime = engineRef.current?.getCurrentTime() ?? 0;
    const pass = activePassSessionRef.current;
    const acceptsTap = pass?.draft.inputMethod === "BOTH" || (captureSegmentationMode === "rhythm-guided" && !redoRangeRef.current);
    if (!acceptsTap || activeRhythmGestureRef.current || !isAudioBoundaryArmed(audioTime, captureArmedAudioTimeRef.current)) return false;
    const relativeTime = audioTime - captureStartAudioTimeRef.current;
    const startTime = pass?.draft.pickup ? relativeTime : Math.max(0, relativeTime);
    activeRhythmGestureRef.current = { pointerId, startTime };
    setActiveRhythmStart(startTime);
    return true;
  }

  function finishRhythmGesture(pointerId?: number): RhythmGuideGesture | null {
    const active = activeRhythmGestureRef.current;
    if (!active || (pointerId !== undefined && pointerId !== active.pointerId)) return null;
    const audioTime = engineRef.current?.getCurrentTime() ?? captureStartAudioTimeRef.current;
    const endTime = Math.max(active.startTime + 0.001, audioTime - captureStartAudioTimeRef.current);
    const next: RhythmGuideGesture = {
      id: rhythmSequenceRef.current,
      sequenceIndex: rhythmGesturesRef.current.length,
      startTime: active.startTime,
      endTime,
      durationMs: (endTime - active.startTime) * 1000,
      startBeat: active.startTime * bpm / 60,
    };
    rhythmSequenceRef.current += 1;
    rhythmGesturesRef.current = [...rhythmGesturesRef.current, next];
    activeRhythmGestureRef.current = null;
    setRhythmGestures(rhythmGesturesRef.current);
    setActiveRhythmStart(null);
    return next;
  }
  async function startRecording() {
    const captureSignal = beginCaptureRun(false);
    setCaptureDetailsOpen(false);
    setCaptureWorkspaceActive(true);
    setHomeTool(null);
    clearPassCaptureState();
    setRhythmEvidenceMode(null);
    setRhythmPickupEnabled(false);
    publishCaptureSession(createNewCaptureSession({
      sessionId: nextCaptureSessionId(),
      inputMethod: captureSegmentationMode === "rhythm-guided" ? "BOTH" : "VOICE",
      tempo: bpm,
      grid,
    }));
    captureAudioEventsRef.current = [];
    setCaptureAudioEvents([]);
    captureArmedAudioTimeRef.current = null;
    lastVisualUpdateRef.current = 0;
    markCaptureAudioEvent("Record button");
    stopPlayback();
    setError(null);
    setEditNotice("");
    setMidiImport(null);
    setManualDurationOverrides({});
    setSelectedNoteIndex(null);
    setEditedNotes(null);
    setNoteUndo([]);
    setNoteRedo([]);
    setInputTestFrame(null);
    setStage("preparing");
    setFrames([]);
    setRawNotes([]);
    setKeys([]);
    setSelectedBar(null);
    setRedoRange(null);
    redoRangeRef.current = null;
    framesRef.current = [];
    resetRhythmCapture(true);
    setRhythmSlots([]);
    keyAnalysisBucketRef.current = -1;
    isStoppingRef.current = false;
    replaceVoiceSource(null);
    try {
      const audio = await ensureWorkspaceAudio("capture");
      if (!audio) { setStage("idle"); return; }
      markCaptureAudioEvent("Audio ready", audio);
      markCaptureAudioEvent("Mic start request", audio);
      await audio.prepareMicrophone(softToneMode);
      markCaptureAudioEvent("Mic start complete", audio);
      markCaptureAudioEvent("Route correction start", audio);
      await restoreCaptureSpeakerOutput();
      markCaptureAudioEvent("Route correction complete", audio);
      await audio.ensureAudioReady();
      markCaptureAudioEvent("Audio barrier ready", audio);
      const countSchedule = await runCountIn(audio, {
        armRhythmInput: captureSegmentationMode === "rhythm-guided",
        onScheduled: (schedule) => audio.startGuide(bpm, referenceMidi, referenceBars, selectedKey?.tonicIndex ?? null, schedule.recordStartTime),
        signal: captureSignal,
      });
      if (captureSignal.aborted) return;
      captureStartAudioTimeRef.current = countSchedule.recordStartTime;
      const plannedRecording = createRecordingTiming(countSchedule.recordStartTime, audioAlignmentMs);
      audio.startCapture();
      lastRecordingTimingRef.current = observeRecordingStart(plannedRecording, audio.getCurrentTime());
      markCaptureAudioEvent("Record start", audio);
      setCaptureSessionStatus("RECORDING", { audioTakeId: `audio-${captureSessionRef.current?.sessionId ?? "capture"}` });
      if (overdubMonitoringEnabled && monitorMidiTracks.length) void audio.playTracks(monitorMidiTracks, bpm);
      setElapsed(Math.max(0, audio.getCurrentTime() - captureStartAudioTimeRef.current));
      setAudioDebug(audio.getDebugInfo());
      setStage("recording");
      sampleTimerRef.current = window.setInterval(() => {
        const time = Math.max(0, audio.getCurrentTime() - captureStartAudioTimeRef.current);
        framesRef.current.push(sampledFrame(audio, time));
        if (time - lastVisualUpdateRef.current >= 0.12) {
          lastVisualUpdateRef.current = time;
          setElapsed(time);
          setFrames(framesRef.current.slice(-420));
        }
        const keyBucket = Math.floor(time * 2);
        if (conversionMode === "key-assist" && keyBucket !== keyAnalysisBucketRef.current) {
          keyAnalysisBucketRef.current = keyBucket;
          const liveFrames = framesRef.current.slice(-440);
          const liveNotes = quantizePitchFramesByBar(liveFrames, bpm, grid, barSettings, micProfile.voicedThreshold, micProfile.confidenceThreshold, noteDetail);
          setRawNotes(liveNotes);
          setKeys(detectKeys(liveNotes));
        }
      }, 55);
    } catch (caught) {
      engineRef.current?.releaseMicrophone();
      captureArmedAudioTimeRef.current = null;
      await releaseCaptureSpeakerOutput();
      if (isAbortError(caught)) return;
      setError(microphoneErrorMessage(caught, "capture"));
      setCaptureSessionStatus("CANCELLED");
      setStage("idle");
      isStoppingRef.current = false;
    }
  }

  async function startBarRedo() {
    if (selectedBar === null || !frames.length) return;
    const captureSignal = beginCaptureRun(true);
    captureAudioEventsRef.current = [];
    setCaptureAudioEvents([]);
    captureArmedAudioTimeRef.current = null;
    lastVisualUpdateRef.current = 0;
    markCaptureAudioEvent("Retake button");
    const range = { start: selectedBar, count: Math.max(1, Math.min(selectedBarCount, totalBars - selectedBar)) };
    stopPlayback();
    setError(null);
    setEditNotice("");
    setMidiImport(null);
    setManualDurationOverrides({});
    setSelectedNoteIndex(null);
    setEditedNotes(null);
    setNoteUndo([]);
    setNoteRedo([]);
    setInputTestFrame(null);
    setStage("preparing");
    setRedoRange(range);
    redoRangeRef.current = range;
    framesBeforeRedoRef.current = [...frames];
    framesRef.current = [];
    keyAnalysisBucketRef.current = -1;
    isStoppingRef.current = false;
    try {
      const audio = await ensureWorkspaceAudio("bar-retake");
      if (!audio) { setStage("result"); return; }
      markCaptureAudioEvent("Audio ready", audio);
      markCaptureAudioEvent("Mic start request", audio);
      await audio.prepareMicrophone(softToneMode);
      markCaptureAudioEvent("Mic start complete", audio);
      markCaptureAudioEvent("Route correction start", audio);
      await restoreCaptureSpeakerOutput();
      markCaptureAudioEvent("Route correction complete", audio);
      await audio.ensureAudioReady();
      markCaptureAudioEvent("Audio barrier ready", audio);
      const countSchedule = await runCountIn(audio, {
        onScheduled: (schedule) => audio.startGuide(bpm, referenceMidi, 0, selectedKey?.tonicIndex ?? null, schedule.recordStartTime),
        signal: captureSignal,
      });
      if (captureSignal.aborted) return;
      captureStartAudioTimeRef.current = countSchedule.recordStartTime;
      const plannedRecording = createRecordingTiming(countSchedule.recordStartTime, audioAlignmentMs);
      audio.startCapture();
      lastRecordingTimingRef.current = observeRecordingStart(plannedRecording, audio.getCurrentTime());
      markCaptureAudioEvent("Record start", audio);
      const barStart = range.start * 4;
      const barEnd = (range.start + range.count) * 4;
      const trimTrack = (track: MidiTrack): MidiTrack => ({
        ...track,
        notes: track.notes.filter((note) => note.startBeat < barEnd && note.startBeat + note.durationBeats > barStart).map((note) => ({
          ...note,
          startBeat: Math.max(0, note.startBeat - barStart),
          durationBeats: Math.min(note.startBeat + note.durationBeats, barEnd) - Math.max(note.startBeat, barStart),
        })),
      });
      if (overdubMonitoringEnabled) {
        const contextTracks = [{ name: "現在のメロディー", role: "melody" as TrackRole, notes }, ...monitorMidiTracks].map(trimTrack).filter((track) => track.notes.length);
        if (contextTracks.length) void audio.playTracks(contextTracks, bpm);
      }
      setAudioDebug(audio.getDebugInfo());
      setStage("recording");
      sampleTimerRef.current = window.setInterval(() => {
        const localTime = Math.max(0, audio.getCurrentTime() - captureStartAudioTimeRef.current);
        framesRef.current.push(sampledFrame(audio, localTime));
        if (localTime - lastVisualUpdateRef.current >= 0.12) {
          lastVisualUpdateRef.current = localTime;
          const startSeconds = barStart * 60 / bpm;
          const endSeconds = barEnd * 60 / bpm;
          const merged = [...framesBeforeRedoRef.current.filter((frame) => frame.time < startSeconds || frame.time >= endSeconds), ...framesRef.current.map((frame) => ({ ...frame, time: frame.time + startSeconds }))].sort((a, b) => a.time - b.time);
          setFrames(merged);
          setElapsed(Math.max(duration, startSeconds + localTime));
        }
      }, 55);
      autoStopTimerRef.current = window.setTimeout(() => void stopRecording(), range.count * 4 * 60_000 / bpm);
    } catch (caught) {
      engineRef.current?.releaseMicrophone();
      captureArmedAudioTimeRef.current = null;
      await releaseCaptureSpeakerOutput();
      if (isAbortError(caught)) return;
      setError(microphoneErrorMessage(caught, "bar-retake"));
      setRedoRange(null);
      redoRangeRef.current = null;
      setStage("result");
    }
  }

  async function stopRecording() {
    if (isStoppingRef.current) return;
    isStoppingRef.current = true;
    setCaptureSessionStatus("STOPPING");
    setStage("stopping");
    if (sampleTimerRef.current !== null) window.clearInterval(sampleTimerRef.current);
    if (autoStopTimerRef.current !== null) window.clearTimeout(autoStopTimerRef.current);
    sampleTimerRef.current = null;
    autoStopTimerRef.current = null;
    const selection = redoRangeRef.current;
    if (activeRhythmGestureRef.current) finishRhythmGesture();
    const blob = await engine().stopCapture();
    captureAbortRef.current = null;
    captureArmedAudioTimeRef.current = null;
    setAudioDebug(engine().getDebugInfo());
    await releaseCaptureSpeakerOutput();
    const activePass = activePassSessionRef.current;
    if (activePass) {
      const draft = activePass.draft;
      const retainedLocalFrames = retainRawPitchEvidence(framesRef.current, micProfile.confidenceThreshold)
        .filter((frame) => draft.pickup || frame.time >= 0);
      const minimumTime = Math.min(0, ...retainedLocalFrames.map((frame) => frame.time));
      const shiftedFrames = retainedLocalFrames.map((frame) => ({ ...frame, time: frame.time - minimumTime }));
      const captureDurationSeconds = Math.max(0, ...retainedLocalFrames.map((frame) => frame.time), rhythmGesturesRef.current.at(-1)?.endTime ?? 0);
      const voiceNotes = quantizePitchFrames(shiftedFrames, bpm, grid, micProfile.voicedThreshold, micProfile.confidenceThreshold, noteDetail)
        .map((note) => ({ ...note, startBeat: note.startBeat + minimumTime * bpm / 60 }));
      const guided = draft.inputMethod === "BOTH" && rhythmGesturesRef.current.length
        ? buildRhythmGuidedNotes(retainedLocalFrames, rhythmGesturesRef.current, {
          bpm,
          grid,
          latencyMs: tapLatencyMs,
          confidenceThreshold: micProfile.confidenceThreshold,
          captureDurationSeconds,
          minimumStartTimeSeconds: draft.pickup ? -beatsPerBar * 60 / bpm : 0,
        })
        : null;
      const localNotes = guided?.notes.length ? guided.notes : voiceNotes;
      const regionStartBeat = activePass.clock.regionStartTick / activePass.clock.ppq;
      const range = passRangeTicks(draft.recordStartBar, draft.lengthBars, activePass.clock.timeSignature, activePass.clock.ppq);
      const endBeat = Number.isFinite(range.endTick) ? range.endTick / activePass.clock.ppq : Number.POSITIVE_INFINITY;
      const createdNotes = localNotes
        .map((note) => ({ ...note, source: draft.inputMethod === "BOTH" ? "TAP_REFINE" as const : "AUTO" as const, startBeat: note.startBeat + regionStartBeat }))
        .filter((note) => note.startBeat < endBeat)
        .map((note) => ({ ...note, durationBeats: Math.max(GRID_BEATS[grid] / 4, Math.min(note.durationBeats, endBeat - note.startBeat)) }));
      const performanceGestures: MelodyTapRawGesture[] = rhythmGesturesRef.current.map((gesture) => ({
        id: gesture.id,
        startTime: activePass.clock.recordStartPerformanceMs + gesture.startTime * 1000,
        endTime: activePass.clock.recordStartPerformanceMs + gesture.endTime * 1000,
      }));
      const globalFrames = retainedLocalFrames.map((frame) => ({ ...frame, time: frame.time + regionStartBeat * 60 / bpm }));
      const effectiveEndTick = Number.isFinite(range.endTick)
        ? range.endTick
        : Math.max(range.startTick + 1, ...createdNotes.map((note) => Math.round((note.startBeat + note.durationBeats) * activePass.clock.ppq)));
      const pass = createInputPass({
        startTick: range.startTick,
        endTick: effectiveEndTick,
        playbackStartBar: draft.playbackStartBar,
        recordStartBar: draft.recordStartBar,
        recordEndBar: draft.recordEndBar,
        untilStop: draft.untilStop,
        replacementRange: draft.replacementRange,
        inputMethod: draft.inputMethod,
        lengthBars: draft.lengthBars,
        preRoll: draft.preRoll,
        pickup: draft.pickup,
        mergeMode: draft.mergeMode,
        bpm,
        ppq: activePass.clock.ppq,
        timeSignature: activePass.clock.timeSignature,
        audioBlob: blob,
        audioOffsetMs: 0,
        f0Evidence: globalFrames,
        tapEvents: buildPassTapEvidence({ gestures: performanceGestures, clock: activePass.clock, gridBeats: GRID_BEATS[grid], pickup: draft.pickup }),
        rhythmSlots: [],
        createdNotes,
      });
      const mergedNotes = mergeInputPassNotes(activePass.beforeNotes, pass);
      const edit = recordMidiEdit(activePass.beforeNotes, mergedNotes, { undo: noteUndo, redo: noteRedo });
      const rangeStartSeconds = range.startTick / activePass.clock.ppq * 60 / bpm;
      const rangeEndSeconds = Number.isFinite(range.endTick) ? range.endTick / activePass.clock.ppq * 60 / bpm : Number.POSITIVE_INFINITY;
      const keptFrames = draft.mergeMode === "REPLACE_RANGE"
        ? activePass.beforeFrames.filter((frame) => frame.time < rangeStartSeconds || frame.time >= rangeEndSeconds)
        : activePass.beforeFrames;
      const mergedFrames = [...keptFrames, ...globalFrames].sort((left, right) => left.time - right.time);
      setEditedNotes(edit.notes);
      setNoteUndo(edit.history.undo);
      setNoteRedo(edit.history.redo);
      setFrames(mergedFrames);
      setRawNotes(mergedNotes);
      setInputPasses((current) => [...current, pass]);
      setKeys(detectKeys(resolvedMidiNotes(mergedNotes)));
      setSelectedNoteIndex(null);
      setManualDurationOverrides({});
      setPlayheadBeat(Math.min(regionStartBeat, ...createdNotes.map((note) => note.startBeat)));
      setElapsed(Math.max(duration, ...mergedNotes.map((note) => (note.startBeat + note.durationBeats) * 60 / bpm)));
      if (blob) replaceVoiceSource(blob);
      setEditorMountKey((value) => value + 1);
      setStage("result");
      setTaskMode("edit");
      setEditNotice(passTargetLabel(draft) + " の " + draft.inputMethod + " Passを追加しました · " + createdNotes.length + " notes。Pass全体は1回のUNDOで戻せます。");
      setCaptureSessionStatus("COMPLETE", {
        audioTakeId: blob ? `audio-${captureSessionRef.current?.sessionId ?? "pass"}` : null,
        rhythmTakeId: rhythmGesturesRef.current.length ? `rhythm-${captureSessionRef.current?.sessionId ?? "pass"}` : null,
      });
      activePassSessionRef.current = null;
      activePassDraftRef.current = null;
      setActivePassDraft(null);
      resetRhythmCapture(false);
      isStoppingRef.current = false;
      return;
    }
    let finishedFrames = [...framesRef.current];
    if (selection !== null) {
      const startSeconds = selection.start * 4 * 60 / bpm;
      const endSeconds = (selection.start + selection.count) * 4 * 60 / bpm;
      finishedFrames = [...framesBeforeRedoRef.current.filter((frame) => frame.time < startSeconds || frame.time >= endSeconds), ...finishedFrames.map((frame) => ({ ...frame, time: frame.time + startSeconds }))].sort((a, b) => a.time - b.time);
      const rangeLabel = selection.count === 1 ? `BAR ${selection.start + 1}` : `BAR ${selection.start + 1}–${selection.start + selection.count}`;
      setEditNotice(`${rangeLabel} の音程解析を歌い直しで置き換えました。録った音声の全体再生は元テイクのままです。`);
    }
    finishedFrames = retainRawPitchEvidence(finishedFrames, micProfile.confidenceThreshold);
    const guidedCapture = selection === null && captureSegmentationMode === "rhythm-guided";
    const captureDurationSeconds = Math.max(elapsed, finishedFrames.at(-1)?.time ?? 0, rhythmGesturesRef.current.at(-1)?.endTime ?? 0);
    const guidedResult = guidedCapture ? buildRhythmGuidedNotes(finishedFrames, rhythmGesturesRef.current, {
      bpm,
      grid,
      latencyMs: tapLatencyMs,
      confidenceThreshold: micProfile.confidenceThreshold,
      captureDurationSeconds,
    }) : null;
    const rawMidiNotes = guidedResult?.notes ?? quantizePitchFramesByBar(finishedFrames, bpm, grid, barSettings, micProfile.voicedThreshold, micProfile.confidenceThreshold, noteDetail);
    if (guidedResult) {
      setRhythmSession({ gestures: guidedResult.gestures, captureDurationSeconds });
      setRhythmGestures(guidedResult.gestures);
      setEditNotice(guidedResult.gestures.length === 0
        ? "タップが記録されませんでした。RHYTHM GUIDEDでは歌いながら音の区切りをタップしてください。"
        : `${guidedResult.gestures.length}回のtap境界から ${guidedResult.notes.length} notesへ変換しました${guidedResult.droppedSegmentCount ? ` · Pitch不足 ${guidedResult.droppedSegmentCount}区間` : ""}。AUTOへ切り替えると同じPitch履歴を自動譜割りでも確認できます。`);
    }
    const pitchNotes = convertWithKeyAssist(conversionMode, finishedFrames, rawMidiNotes, bpm, keyAssistStrength, new Set(), keyAssistWeight).notes;
    const minimumNotes = ensureMinimumNoteDurationByBar(pitchNotes, lengthAssist ? minimumNoteBeats : null, barSettings);
    const midiNotes = voiceMapAssist && activeVoiceProfile ? applyVoiceSustainAssist(minimumNotes, activeVoiceProfile, GRID_BEATS[grid]) : minimumNotes;
    const candidates = detectKeys(midiNotes);
    setFrames(finishedFrames);
    setRawNotes(rawMidiNotes);
    setKeys(candidates);
    if (selection === null) setSelectedKey(candidates[0] ?? selectedKey);
    if (selection === null && blob) {
      replaceVoiceSource(blob);
    }
    setRedoRange(null);
    redoRangeRef.current = null;
    setStage("result");
    setCaptureSessionStatus("COMPLETE", {
      audioTakeId: blob ? `audio-${captureSessionRef.current?.sessionId ?? "capture"}` : null,
      rhythmTakeId: rhythmGesturesRef.current.length ? `rhythm-${captureSessionRef.current?.sessionId ?? "capture"}` : null,
    });
    isStoppingRef.current = false;
  }
  function currentAnalysisSettings(): AnalysisSettings {
    return { bpm, grid, noteDetail, minimumNoteBeats, lengthAssist, conversionMode, keyAssistStrength, keyAssistWeight };
  }

  function applyAnalysisSettings(settings: AnalysisSettings) {
    setBpm(settings.bpm);
    setGrid(settings.grid);
    setNoteDetail(settings.noteDetail);
    setMinimumNoteBeats(settings.minimumNoteBeats);
    setLengthAssist(settings.lengthAssist);
    setConversionMode(settings.conversionMode);
    setKeyAssistStrength(settings.keyAssistStrength);
    setKeyAssistWeight(settings.keyAssistWeight);
  }

  function openConcierge(phase: "before" | "after") {
    setConciergePhase(phase);
    setConciergeProposal(null);
    setConciergeBefore(null);
    setConciergeOpen(true);
  }

  function chooseConciergeIssue(issue: GuidedIssue) {
    setConciergeProposal(buildConciergeProposal(issue, currentAnalysisSettings()));
  }

  function reanalyzeWithConcierge() {
    if (!conciergeProposal) return;
    if (editedNotes) {
      setEditNotice("手動EDIT済みノートは自動再解析しません。UNDOで自動結果へ戻すか、新しいテイクで調整アシスタントを使ってください。");
      setConciergeOpen(false);
      return;
    }
    setConciergeBefore({ settings: currentAnalysisSettings(), noteCount: notes.length });
    const proposedSettings = conciergeProposal.needsTempoReview ? { ...conciergeProposal.settings, bpm, grid } : conciergeProposal.settings;
    applyAnalysisSettings(proposedSettings);
    setConciergeProposal(null);
    setEditNotice("同じ歌声の測定データを新しい設定で再解析しました。調整アシスタントでBEFORE / AFTERを確認できます。");
  }

  function revertConcierge() {
    if (!conciergeBefore) return;
    applyAnalysisSettings(conciergeBefore.settings);
    setConciergeBefore(null);
    setConciergeProposal(null);
    setEditNotice("コンシェルジュ適用前の設定へ戻しました。");
  }

  function adoptConcierge() {
    setConciergeBefore(null);
    setConciergeProposal(null);
    setConciergeOpen(false);
    setEditNotice("再解析結果を採用しました。設定はSETTINGSのADVANCEDでも確認できます。");
  }

  async function startMelodyTapAudio(): Promise<boolean> {
    stopPlayback();
    engineRef.current?.stopGuide();
    melodyTapCancelRef.current = false;
    setError(null);
    const previousStage = stage;
    const audio = await ensureWorkspaceAudio("melody-tap");
    if (!audio) return false;
    try {
      await runCountIn(audio, {
        onScheduled: (schedule) => audio.startGuide(bpm, referenceMidi, 0, selectedKey?.tonicIndex ?? null, schedule.recordStartTime),
      });
      if (melodyTapCancelRef.current) {
        audio.stopGuide();
        setStage(previousStage);
        return false;
      }
      setStage(previousStage);
      return true;
    } catch (caught) {
      setStage(previousStage);
      setError(caught instanceof Error ? caught.message : "メトロノームを開始できませんでした。");
      return false;
    }
  }

  function stopMelodyTapAudio() {
    melodyTapCancelRef.current = true;
    engineRef.current?.stopGuide();
  }

  function stopRhythmEvidenceAudio() {
    engineRef.current?.stopGuide();
    voiceAudioRef.current?.pause();
    voiceAudioRef.current = null;
    clearPlaybackTimers();
    releaseRhythmPlaybackUrl();
    setIsPlaying(false);
    setPlaybackLabel("");
  }

  function closeRhythmEvidence() {
    stopRhythmEvidenceAudio();
    melodyTapCancelRef.current = true;
    captureArmedAudioTimeRef.current = null;
    activeRhythmGestureRef.current = null;
    rhythmGesturesRef.current = [];
    setActiveRhythmStart(null);
    setRhythmGestures([]);
    setCount(null);
    setRhythmRefinePreview(null);
    setCaptureSessionStatus("CANCELLED");
    rhythmEvidenceOriginRef.current = null;
    clearPassCaptureState();
    setRhythmEvidenceMode(null);
    setStage(notes.length ? "result" : "idle");
  }

  function openRhythmEvidence(mode: RhythmEvidenceMode) {
    setCaptureWorkspaceActive(true);
    if (mode === "rhythm-refine") {
      const latestVoicePass = [...inputPasses].reverse().find((pass) => (pass.inputMethod === "VOICE" || pass.inputMethod === "BOTH") && pass.f0Evidence.length > 0 && pass.audioBlob);
      if (latestVoicePass) {
        refineTargetPassRef.current = latestVoicePass;
        activePassDraftRef.current = {
          playbackStartBar: latestVoicePass.playbackStartBar,
          recordStartBar: latestVoicePass.recordStartBar,
          recordEndBar: latestVoicePass.recordEndBar,
          untilStop: latestVoicePass.untilStop,
          replacementRange: latestVoicePass.replacementRange,
          selectedRange: { startBar: latestVoicePass.recordStartBar, endBar: latestVoicePass.recordEndBar ?? latestVoicePass.recordStartBar },
          selectionMode: null,
          lengthBars: latestVoicePass.lengthBars,
          inputMethod: "RHYTHM",
          preRoll: latestVoicePass.preRoll,
          pickup: latestVoicePass.pickup,
          mergeMode: "REPLACE_RANGE",
        };
        setActivePassDraft(activePassDraftRef.current);
        replaceVoiceSource(latestVoicePass.audioBlob);
        setAudioAlignmentMs(latestVoicePass.audioOffsetMs);
      } else if (!voiceBlobRef.current || !frames.length) {
        setEditNotice("先に通常CAPTUREまたはVOICE PASSで声とPitch evidenceを記録してください。");
        return;
      } else {
        setAudioAlignmentMs(baseAudioOffsetMs);
      }
    } else {
      refineTargetPassRef.current = null;
      if (!activePassDraftRef.current) {
        clearPassCaptureState();
        setRhythmPickupEnabled(false);
      }
    }
    if (!activePassDraftRef.current && editedNotes && !window.confirm("現在の手動EDIT内容は、新しい譜割りで置き換わります。元のF0から再計算してよろしいですか？")) return;
    if (!activePassDraftRef.current) {
      publishCaptureSession(createNewCaptureSession({
        sessionId: nextCaptureSessionId(),
        mode: "RHYTHM_ONLY",
        inputMethod: "RHYTHM",
        tempo: bpm,
        grid,
      }));
    }
    setRhythmEvidenceMode(mode);
  }

  function adjustAudioAlignment(deltaMs: number | "reset") {
    const requested = deltaMs === "reset" ? 0 : audioAlignmentMs + deltaMs;
    const next = planAudioAlignment(requested).offsetMs;
    setAudioAlignmentMs(next);
    if (refineTargetPassRef.current) {
      const updated = { ...refineTargetPassRef.current, audioOffsetMs: next };
      refineTargetPassRef.current = updated;
      setInputPasses((current) => current.map((pass) => pass.id === updated.id ? updated : pass));
    } else {
      setBaseAudioOffsetMs(next);
    }
    stopRhythmEvidenceAudio();
  }

  function passTargetLabel(draft: InputPassDraft): string {
    const length = draft.lengthBars === null ? "UNTIL STOP" : draft.lengthBars + " BAR" + (draft.lengthBars > 1 ? "S" : "");
    return "BAR " + (draft.recordStartBar + 1) + " · " + length;
  }

  function beginInputPass(draft: InputPassDraft) {
    setCaptureWorkspaceActive(true);
    setHomeTool(null);
    const range = passRangeTicks(draft.recordStartBar, draft.lengthBars, currentTimeSignature, currentPpq);
    if (regionHasNotes(notes, range.startTick, range.endTick, currentPpq)) {
      const action = draft.mergeMode === "REPLACE_RANGE" ? "指定範囲を置き換えます。" : "既存ノートを残して追加します。";
      if (!window.confirm("この範囲には既存ノートがあります。" + action + " 続けますか？")) return;
    }
    stopPlayback();
    setAddPassOpen(false);
    activePassDraftRef.current = draft;
    setActivePassDraft(draft);
    refineTargetPassRef.current = null;
    publishCaptureSession(createAddPassCaptureSession({
      sessionId: nextCaptureSessionId(),
      draft,
      tempo: bpm,
      grid,
      ppq: currentPpq,
      timeSignature: currentTimeSignature,
    }));
    if (draft.inputMethod === "RHYTHM") {
      setTaskMode("capture");
      setRhythmPickupEnabled(draft.pickup);
      setRhythmEvidenceMode("rhythm-first");
      return;
    }
    void startInputPassRecording(draft);
  }

  async function startInputPassRecording(draft: InputPassDraft) {
    const captureSignal = beginCaptureRun(notes.length > 0);
    if (!captureSessionRef.current || captureSessionRef.current.mode !== "ADD_PASS") {
      publishCaptureSession(createAddPassCaptureSession({
        sessionId: nextCaptureSessionId(),
        draft,
        tempo: bpm,
        grid,
        ppq: currentPpq,
        timeSignature: currentTimeSignature,
      }));
    }
    captureAudioEventsRef.current = [];
    setCaptureAudioEvents([]);
    captureArmedAudioTimeRef.current = null;
    lastVisualUpdateRef.current = Number.NEGATIVE_INFINITY;
    setError(null);
    setEditNotice("");
    setStage("preparing");
    setTaskMode("capture");
    setSelectedNoteIndex(null);
    framesRef.current = [];
    resetRhythmCapture(true);
    isStoppingRef.current = false;
    const beforeNotes = notes.map((note) => ({ ...note }));
    const beforeFrames = frames.map((frame) => ({ ...frame }));
    const startTick = passRangeTicks(draft.recordStartBar, draft.lengthBars, currentTimeSignature, currentPpq).startTick;
    try {
      const audio = await ensureWorkspaceAudio("capture");
      if (!audio) {
        activePassDraftRef.current = null;
        setActivePassDraft(null);
        setStage(notes.length ? "result" : "idle");
        setCaptureSessionStatus("CANCELLED");
        return;
      }
      await audio.prepareMicrophone(softToneMode);
      await restoreCaptureSpeakerOutput();
      await audio.ensureAudioReady();
      const listenBars = preRollBarCount(draft.preRoll);
      const countBeats = draft.preRoll === "COUNT_IN" ? 4 : Math.max(1, Math.round(beatsPerBar * listenBars));
      let sampling = false;
      const startSampling = () => {
        if (sampling) return;
        sampling = true;
        audio.startCapture();
        sampleTimerRef.current = window.setInterval(() => {
          const audioTime = audio.getCurrentTime();
          const relativeTime = audioTime - captureStartAudioTimeRef.current;
          const session = activePassSessionRef.current;
          const raw = sampledFrame(audio, draft.pickup ? relativeTime : Math.max(0, relativeTime));
          const frame = suppressCountClickTransient(raw, audioTime, session?.clickTimes ?? []);
          framesRef.current.push(frame);
          if (relativeTime - lastVisualUpdateRef.current >= 0.12) {
            lastVisualUpdateRef.current = relativeTime;
            setElapsed(Math.max(0, relativeTime));
            setFrames(framesRef.current.slice(-420));
          }
        }, 55);
      };
      const schedule = await runCountIn(audio, {
        beats: countBeats,
        emitClicks: draft.preRoll === "COUNT_IN",
        sessionStatus: listenBars > 0 ? "PRE_ROLL" : "COUNT_IN",
        armRhythmInput: draft.inputMethod === "BOTH",
        inputDuringCount: draft.pickup && draft.preRoll === "COUNT_IN",
        onScheduled: (scheduled) => {
          captureStartAudioTimeRef.current = scheduled.recordStartTime;
          const recordStartPerformanceMs = performance.now() + (scheduled.recordStartTime - audio.getCurrentTime()) * 1000;
          activePassSessionRef.current = {
            draft,
            beforeNotes,
            beforeFrames,
            clickTimes: draft.preRoll === "COUNT_IN" ? scheduled.beatTimes : [],
            clock: createMusicalClock({
              bpm,
              ppq: currentPpq,
              timeSignature: currentTimeSignature,
              regionStartTick: startTick,
              recordStartAudioTime: scheduled.recordStartTime,
              recordStartPerformanceMs,
              timingOffsets: deviceTimingOffsets,
            }),
          };
          if (listenBars > 0) {
            void audio.schedulePreviewRangeAt(resolvedNotes, bpm, draft.playbackStartBar * beatsPerBar, draft.recordStartBar * beatsPerBar, scheduled.startTime, previewInstrument);
          }
          audio.startGuide(bpm, referenceMidi, 0, selectedKey?.tonicIndex ?? null, scheduled.recordStartTime);
          if (draft.pickup && draft.preRoll === "COUNT_IN") startSampling();
        },
        signal: captureSignal,
      });
      if (captureSignal.aborted) return;
      captureStartAudioTimeRef.current = schedule.recordStartTime;
      if (!sampling) startSampling();
      setAudioDebug(audio.getDebugInfo());
      setStage("recording");
      setCaptureSessionStatus("RECORDING", { audioTakeId: `audio-${captureSessionRef.current?.sessionId ?? "pass"}` });
      if (draft.lengthBars !== null) {
        autoStopTimerRef.current = window.setTimeout(() => void stopRecording(), draft.lengthBars * beatsPerBar * 60_000 / bpm);
      }
    } catch (caught) {
      engineRef.current?.releaseMicrophone();
      captureArmedAudioTimeRef.current = null;
      activePassSessionRef.current = null;
      activePassDraftRef.current = null;
      setActivePassDraft(null);
      await releaseCaptureSpeakerOutput();
      if (isAbortError(caught)) return;
      setError(microphoneErrorMessage(caught, "add-pass"));
      setCaptureSessionStatus("CANCELLED");
      setStage(notes.length ? "result" : "idle");
    }
  }

  async function startRhythmEvidenceAudio(): Promise<boolean> {
    stopRhythmEvidenceAudio();
    setError(null);
    const draft = activePassDraftRef.current;
    const audio = await ensureWorkspaceAudio(rhythmEvidenceMode === "rhythm-refine" ? "recorded-voice" : "melody-tap");
    if (!audio) return false;
    const listenBars = draft ? preRollBarCount(draft.preRoll) : 0;
    const countBeats = !draft || draft.preRoll === "COUNT_IN" ? 4 : Math.max(1, Math.round(beatsPerBar * listenBars));
    try {
      await runCountIn(audio, {
        beats: countBeats,
        emitClicks: !draft || draft.preRoll === "COUNT_IN",
        sessionStatus: draft && listenBars > 0 ? "PRE_ROLL" : "COUNT_IN",
        onScheduled: (schedule) => {
          if (rhythmEvidenceMode !== "rhythm-refine") {
            rhythmEvidenceOriginRef.current = performance.now() + (schedule.recordStartTime - audio.getCurrentTime()) * 1000;
          }
          if (draft && listenBars > 0) {
            void audio.schedulePreviewRangeAt(resolvedNotes, bpm, draft.playbackStartBar * beatsPerBar, draft.recordStartBar * beatsPerBar, schedule.startTime, previewInstrument);
          }
          audio.startGuide(bpm, referenceMidi, 0, selectedKey?.tonicIndex ?? null, schedule.recordStartTime);
        },
      });

      setCaptureSessionStatus(rhythmEvidenceMode === "rhythm-refine" ? "SECOND_PASS" : "RECORDING");
      if (rhythmEvidenceMode !== "rhythm-refine") return true;
      const blob = refineTargetPassRef.current?.audioBlob ?? voiceBlobRef.current;
      if (!blob || blob.size === 0) throw new Error("保存音声Blobが空です");
      const sourceUrl = URL.createObjectURL(blob);
      rhythmPlaybackUrlRef.current = sourceUrl;
      const recordedVoice = new Audio(sourceUrl);
      recordedVoice.preload = "auto";
      voiceAudioRef.current = recordedVoice;
      console.info("[P-013 audio] starting VOICE→TAP source", { mimeType: blob.type, size: blob.size, canPlay: recordedVoice.canPlayType(blob.type) });
      const alignment = planAudioAlignment(audioAlignmentMs);
      if (alignment.seekSeconds > 0) {
        if (recordedVoice.readyState < 1) {
          await new Promise<void>((resolve, reject) => {
            recordedVoice.addEventListener("loadedmetadata", () => resolve(), { once: true });
            recordedVoice.addEventListener("error", () => reject(new Error("録音音声の情報を読めませんでした")), { once: true });
          });
        }
        recordedVoice.currentTime = Math.min(alignment.seekSeconds, Math.max(0, recordedVoice.duration - 0.01));
      }
      if (alignment.delayMs > 0) await sleep(alignment.delayMs);
      await recordedVoice.play();
      rhythmEvidenceOriginRef.current = correctedAudioOriginMs(performance.now(), recordedVoice.currentTime); // eslint-disable-line react-hooks/purity
      setIsPlaying(true);
      setPlaybackLabel(`準備完了 · BAR ${draft?.recordStartBar !== undefined ? draft.recordStartBar + 1 : 1}からタップ`);
      recordedVoice.onended = () => {
        setIsPlaying(false);
        setPlaybackLabel("");
        releaseRhythmPlaybackUrl();
      };
      return true;
    } catch (caught) {
      const blob = refineTargetPassRef.current?.audioBlob ?? voiceBlobRef.current;
      const detail = caught instanceof Error ? caught.message : String(caught);
      console.error("[P-013 audio] VOICE→TAP playback failed", { mimeType: blob?.type ?? null, size: blob?.size ?? 0, cyclePassId: refineTargetPassRef.current?.id ?? "base-capture" }, caught);
      voiceAudioRef.current = null;
      releaseRhythmPlaybackUrl();
      setError(`元音声を再生できませんでした（${blob?.type || "MIME不明"} / ${blob?.size ?? 0} bytes）。録音をやり直さず、診断情報を確認できます。 ${detail}`);
      setCaptureSessionStatus("CANCELLED");
      return false;
    }
  }

  function createNotesFromRhythmEvidence(rawGestures: MelodyTapRawGesture[], analysis: MelodyTapAnalysis) {
    const mode = rhythmEvidenceMode;
    if (!mode || !rawGestures.length) return;
    stopRhythmEvidenceAudio();
    const source = mode === "rhythm-refine" ? "TAP_REFINE" : "RHYTHM_FIRST";
    const draft = activePassDraftRef.current;
    const regionStartTick = draft ? draft.recordStartBar * beatsPerBar * currentPpq : 0;
    const localSlots = buildRhythmSlotsFromMelodyTaps(rawGestures, bpm, {
      source,
      grid: analysis.recommendedDivision,
      originTimeMs: rhythmEvidenceOriginRef.current ?? undefined,
      finalDurationSeconds: mode === "rhythm-refine" ? (draft?.lengthBars === null || draft?.lengthBars === undefined ? duration : draft.lengthBars * beatsPerBar * 60 / bpm) : undefined,
      ppq: currentPpq,
    });
    const slots = draft ? localSlots.map((slot) => ({
      ...slot,
      startTick: slot.startTick + regionStartTick,
      endTick: slot.endTick + regionStartTick,
      startBeat: slot.startBeat + regionStartTick / currentPpq,
    })) : localSlots;
    const evidence = (refineTargetPassRef.current?.f0Evidence ?? frames).map((frame) => ({ ...frame, cents: frame.cents ?? null, voiced: frame.voiced ?? (frame.midiFloat !== null && frame.confidence >= (frame.acceptanceConfidence ?? micProfile.confidenceThreshold)) }));
    const assignedSlots = mode === "rhythm-refine"
      ? assignPitchEvidenceToSlots(evidence, slots, { bpm, confidenceThreshold: micProfile.confidenceThreshold })
      : slots;
    const fallbackMidi = resolvedNotes.length
      ? Math.round(resolvedNotes.reduce((sum, note) => sum + note.midi, 0) / resolvedNotes.length)
      : 60;
    const editable = rhythmSlotsToEditableNotes(assignedSlots, fallbackMidi);
    const resolved = resolvedMidiNotes(editable);
    setRhythmSlots(assignedSlots);

    if (draft) {
      const range = passRangeTicks(draft.recordStartBar, draft.lengthBars, currentTimeSignature, currentPpq);
      const effectiveEndTick = Number.isFinite(range.endTick)
        ? range.endTick
        : Math.max(range.startTick + 1, ...editable.map((note) => Math.round((note.startBeat + note.durationBeats) * currentPpq)));
      const clock = createMusicalClock({
        bpm,
        ppq: currentPpq,
        timeSignature: currentTimeSignature,
        regionStartTick: range.startTick,
        recordStartAudioTime: 0,
        recordStartPerformanceMs: rhythmEvidenceOriginRef.current ?? rawGestures[0].startTime,
        timingOffsets: deviceTimingOffsets,
      });
      const pass = createInputPass({
        startTick: range.startTick,
        endTick: effectiveEndTick,
        playbackStartBar: draft.playbackStartBar,
        recordStartBar: draft.recordStartBar,
        recordEndBar: draft.recordEndBar,
        untilStop: draft.untilStop,
        replacementRange: draft.replacementRange,
        inputMethod: "RHYTHM",
        lengthBars: draft.lengthBars,
        preRoll: draft.preRoll,
        pickup: draft.pickup,
        mergeMode: draft.mergeMode,
        bpm,
        ppq: currentPpq,
        timeSignature: currentTimeSignature,
        audioBlob: refineTargetPassRef.current?.audioBlob ?? null,
        audioOffsetMs: audioAlignmentMs,
        f0Evidence: evidence,
        tapEvents: buildPassTapEvidence({ gestures: rawGestures, clock, gridBeats: GRID_BEATS[grid], pickup: draft.pickup }),
        rhythmSlots: assignedSlots,
        createdNotes: editable,
      });
      const before = notes.map((note) => ({ ...note }));
      const merged = mergeInputPassNotes(before, pass);
      const edit = recordMidiEdit(before, merged, { undo: noteUndo, redo: noteRedo });
      setEditedNotes(edit.notes);
      setNoteUndo(edit.history.undo);
      setNoteRedo(edit.history.redo);
      setInputPasses((current) => [...current, pass]);
      setSelectedNoteIndex(null);
      setManualDurationOverrides({});
      setKeys(detectKeys(resolvedMidiNotes(merged)));
      setPlayheadBeat(Math.min(regionStartTick / currentPpq, ...editable.map((note) => note.startBeat)));
      setEditorMountKey((value) => value + 1);
      setStage("result");
      setTaskMode("edit");
      setEditNotice(mode === "rhythm-refine"
        ? passTargetLabel(draft) + " の保存済みF0へ新しいRhythm Slotを適用しました。1回のUNDOでPass全体を戻せます。"
        : passTargetLabel(draft) + " に " + assignedSlots.length + "個のRhythm Slotを追加しました。灰色の?はVOICEでPitchを付けられます。");
    } else {
      setEditedNotes(editable);
      setNoteUndo([]);
      setNoteRedo([]);
      setSelectedNoteIndex(null);
      setManualDurationOverrides({});
      setKeys(detectKeys(resolved));
      if (mode === "rhythm-first") {
        setFrames([]);
        setRawNotes([]);
        setRhythmSession(null);
        setElapsed(Math.max(0, ...editable.map((note) => (note.startBeat + note.durationBeats) * 60 / bpm)));
      }
      setStage("result");
      setTaskMode("edit");
      setEditNotice(mode === "rhythm-refine"
        ? "保存済みRaw F0を " + assignedSlots.length + "個のRhythm Slotへ再割当しました · Pitch未確定 " + (assignedSlots.length - resolved.length) + "音。"
        : assignedSlots.length + "個のRhythm Slotを作りました。灰色の?をVOICEで歌うとPitchを付けられます。");
    }
    activePassDraftRef.current = null;
    setActivePassDraft(null);
    refineTargetPassRef.current = null;
    setRhythmEvidenceMode(null);
    setCaptureSessionStatus("COMPLETE", { rhythmTakeId: `rhythm-${captureSessionRef.current?.sessionId ?? "capture"}` });
  }

  function applyMelodyTapRecommendation(analysis: MelodyTapAnalysis) {
    if (!melodyTapUndo) setMelodyTapUndo(currentAnalysisSettings());
    setGrid(analysis.recommendation.grid);
    setMinimumNoteBeats(analysis.recommendation.minimumNoteBeats);
    setLengthAssist(analysis.recommendation.lengthAssist);
    setNoteDetail(analysis.recommendation.noteDetail);
    setEditNotice("MELODY TAPの提案を適用しました。GRID " + analysis.recommendation.grid + " · MIN NOTE " + analysis.recommendation.minimumNoteBeats + " beat · NOTE DETAIL " + analysis.recommendation.noteDetail);
  }

  function restoreMelodyTapRecommendation() {
    if (!melodyTapUndo) return;
    applyAnalysisSettings(melodyTapUndo);
    setMelodyTapUndo(null);
    setEditNotice("MELODY TAP適用前のGRID・MIN NOTE・NOTE DETAILへ戻しました。");
  }

  async function tapTempo() {
    const now = performance.now(); // eslint-disable-line react-hooks/purity
    const previous = tapTimesRef.current.at(-1);
    const next = previous && now - previous <= 2000 ? [...tapTimesRef.current, now].slice(-9) : [now];
    tapTimesRef.current = next;
    setTapCount(next.length);
    const detectedBpm = calculateTapTempo(next);
    if (detectedBpm) setBpm(detectedBpm);
    const audio = await ensureWorkspaceAudio("tap-tempo");
    if (audio) await audio.playClick(next.length === 1, selectedKey?.tonicIndex ?? null);
  }

  async function playVoice() {
    if (!voiceUrl) return;
    stopPlayback();
    if (!await ensureWorkspaceAudio("recorded-voice")) return;
    const audio = new Audio(voiceUrl);
    voiceAudioRef.current = audio;
    setIsPlaying(true);
    setPlaybackLabel("録った音声");
    setPlayheadBeat(0);
    const update = () => {
      if (audio.paused || audio.ended) return;
      setPlayheadBeat(audio.currentTime * bpm / 60);
      playbackFrameRef.current = window.requestAnimationFrame(update);
    };
    audio.onended = clearPlaybackState;
    await audio.play();
    playbackFrameRef.current = window.requestAnimationFrame(update);
  }

  async function playConvertedMidi() {
    haltEditorPlayback();
    if (!resolvedNotes.length) {
      setEditNotice(`Pitch未確定 ${unresolvedNoteCount}音があります。VOICEでPitchを付けると再生できます。`);
      return;
    }
    const endBeat = Math.max(0, ...resolvedNotes.map((note) => note.startBeat + note.durationBeats));
    const startBeat = playbackStartBeat(playheadBeat, endBeat);
    const audio = await ensureWorkspaceAudio("edit-play");
    if (!audio) return;
    const milliseconds = await audio.playPreviewMidi(resolvedNotes, bpm, startBeat, previewInstrument);
    beginEditorPlaybackProgress(milliseconds, previewInstrument === "piano" ? "変換MIDI · PIANO" : "変換MIDI · HOLD", startBeat);
  }

  function pauseEditorPlayback() {
    haltEditorPlayback();
    setEditNotice("一時停止しました。PLAYで現在位置から再開します。");
  }

  function stopEditorPlayback() {
    haltEditorPlayback();
    setEditNotice("停止しました。再生位置は保持しています。");
  }

  function goToEditorStart() {
    haltEditorPlayback();
    setPlayheadBeat(Math.min(0, ...resolvedNotes.map((note) => note.startBeat)));
    setEditNotice("PICKUPを含む曲頭へ戻りました。");
  }

  function previousEditorBar() {
    haltEditorPlayback();
    setPlayheadBeat((current) => previousBarStart(current ?? 0, currentTimeSignature));
    setEditNotice("1小節前の先頭へ移動しました。");
  }
  async function playRawMidi() {
    stopPlayback();
    const audio = await ensureWorkspaceAudio("raw-midi");
    if (!audio) return;
    const milliseconds = await audio.playMidi(rawNotes, bpm, trackRole);
    beginPlaybackProgress(milliseconds, "RAW MIDI");
  }

  async function playSelectedMidi() {
    stopPlayback();
    const audio = await ensureWorkspaceAudio("arrangement-play");
    if (!audio) return;
    const milliseconds = await audio.playMidi(selectedMidi, bpm, exportMix ? "strings" : trackRole);
    beginPlaybackProgress(milliseconds, exportMix ? "メロディー＋伴奏" : "メロディー");
  }

  async function playTrackStack(trackList: MidiTrack[] = activeTracks) {
    stopPlayback();
    const audio = await ensureWorkspaceAudio("track-play");
    if (!audio) return;
    const milliseconds = await audio.playTracks(trackList, bpm);
    beginPlaybackProgress(milliseconds, "トラック再生");
  }

  function applyImportedTrack(session: MidiImportSession, trackIndex: number) {
    const track = session.tracks.find((candidate) => candidate.index === trackIndex);
    if (!track) return;
    haltEditorPlayback();
    if (editModeRef.current === "voice") leaveVoiceDrawMode("select");
    else {
      editModeRef.current = "select";
      setEditMode("select");
    }
    const importedNotes = track.notes.map((note) => ({ ...note }));
    framesRef.current = [];
    setFrames([]);
    setRawNotes(importedNotes);
    setEditedNotes(importedNotes);
    setManualDurationOverrides({});
    setNoteUndo([]);
    setNoteRedo([]);
    setSelectedNoteIndex(null);
    setTracks([]);
    setClips([]);
    setSelectedTrackId(null);
    setSelectedClipId(null);
    setChordOverrides({});
    setElapsed(0);
    setBpm(Math.max(20, Math.min(300, session.bpm)));
    setProjectTiming(session.timing);
    setTrackName(track.name);
    setTrackRole("melody");
    const detected = detectKeys(importedNotes);
    setKeys(detected);
    setSelectedKey(null);
    setStage("result");
    setMidiImport({ ...session, selectedTrackIndex: track.index });
    setEditorMountKey((value) => value + 1);
    setTaskMode("edit");
    setEditNotice("MIDIを共通EDITモデルへ読み込みました: " + track.name + " · " + track.notes.length + " notes");
    setError(null);
  }

  async function openMidiForEditing() {
    if (notes.length && !window.confirm("現在のEDIT内容を置き換えてMIDIファイルを開きます。よろしいですか？")) return;
    setEditNotice("MIDIファイルを選択してください…");
    try {
      const opened = await openMidiFile();
      if (!opened) {
        setEditNotice("MIDIファイルの選択をキャンセルしました。");
        return;
      }
      const parsed = parseMidiFile(opened.bytes);
      const firstTrack = parsed.tracks[0];
      const session: MidiImportSession = {
        ...parsed,
        fileName: opened.fileName,
        selectedTrackIndex: firstTrack.index,
      };
      applyImportedTrack(session, firstTrack.index);
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : String(caught);
      setError("MIDIファイルを開けませんでした。" + (message ? " (" + message + ")" : ""));
      setEditNotice("");
    }
  }
  async function saveMidi(bytes: Uint8Array, fileName: string, omittedUnresolved = 0): Promise<boolean> {
    if (isExporting) return false;
    setIsExporting(true);
    setExportNotice(omittedUnresolved ? `Pitch未確定 ${omittedUnresolved}音は出力せず、解決済みノートだけ保存します。保存先を選択してください…` : "保存先を選択してください…");
    try {
      const result = await saveMidiFile(bytes, fileName);
      const omitted = omittedUnresolved ? ` · Pitch未確定 ${omittedUnresolved}音は未出力` : "";
      const action = result.destination === "android-document"
        ? `保存しました: ${result.fileName}`
        : result.destination === "web-share"
          ? `共有しました: ${result.fileName} · iPhoneでは共有先から「ファイルに保存」も選べます`
          : `ダウンロードしました: ${result.fileName}`;
      setExportNotice(action + omitted);
      setExportCounter((current) => Math.min(9999, current + 1));
      setError(null);
      return true;
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : String(caught);
      setExportNotice(/cancel|abort|キャンセル/i.test(message) ? "保存・共有をキャンセルしました。" : `保存できませんでした: ${message}`);
      return false;
    } finally {
      setIsExporting(false);
    }
  }

  function exportMidi() {
    if (!selectedMidi.length) {
      setExportNotice(`Pitch未確定 ${unresolvedNoteCount}音はMIDIへ推測出力しません。VOICEでPitchを付けてください。`);
      return;
    }
    const detail = exportMix && accompaniment.length ? "伴奏あり" : undefined;
    const fileName = buildMidiExportFileName({ projectTitle, memo: exportMemo, detail, date: new Date(), version: exportCounter });
    void saveMidi(buildMidiFile(selectedMidi, bpm, projectTitle, trackRole, currentMidiTiming), fileName, unresolvedNoteCount);
  }

  function exportTrack(track: ProjectTrack) {
    const midiTrack = trackToMidiTrack(track, clips, currentPpq);
    const fileName = buildMidiExportFileName({ projectTitle, memo: exportMemo, detail: track.name, date: new Date(), version: exportCounter });
    void saveMidi(buildMidiFile(midiTrack.notes, bpm, track.name, track.role, currentMidiTiming), fileName);
  }

  function exportTrackStack() {
    if (!exportMidiTracks.length) return;
    const fileName = buildMidiExportFileName({ projectTitle, memo: exportMemo, detail: "全トラック", date: new Date(), version: exportCounter });
    void saveMidi(buildMultiTrackMidiFile(exportMidiTracks, bpm, currentMidiTiming), fileName);
  }

  function addCurrentTrack() {
    if (!resolvedNotes.length) return;
    const name = trackName.trim() || `${ROLE_LABELS[trackRole]} ${tracks.length + 1}`;
    const pair = createTrackWithClip({
      trackId: `track-${nextTrackIdRef.current++}`,
      clipId: `clip-${nextClipIdRef.current++}`,
      name,
      role: trackRole,
      notes: resolvedNotes,
      ppq: currentPpq,
      recordingTiming: lastRecordingTimingRef.current,
    });
    setTracks((current) => [...current, pair.track]);
    setClips((current) => [...current, pair.clip]);
    setSelectedTrackId(pair.track.id);
    setSelectedClipId(pair.clip.id);
    setTrackName(`${ROLE_LABELS[trackRole]} ${tracks.length + 2}`);
  }

  function updateTrack(id: string, patch: Partial<ProjectTrack>) {
    setTracks((current) => current.map((track) => track.id === id ? { ...track, ...patch, id: track.id, clipIds: track.clipIds } : track));
  }

  function playProjectTrack(track: ProjectTrack) {
    void playTrackStack([trackToMidiTrack(track, clips, currentPpq)]);
  }

  function cycleChord(bar: number) {
    const suggestion = chordPlan.find((item) => item.bar === bar);
    if (!suggestion) return;
    const safeCandidates = suggestion.candidates.filter((candidate) => !candidate.warning);
    const candidates = safeCandidates.length >= 2 ? safeCandidates : suggestion.candidates;
    const currentIndex = candidates.findIndex((candidate) => candidate.degree === suggestion.degree);
    const next = candidates[(Math.max(0, currentIndex) + 1) % candidates.length];
    setChordOverrides((current) => ({ ...current, [bar]: next.degree }));
  }

  function changeHarmonyRoute(route: HarmonyRoute) {
    setHarmonyRoute(route);
    setChordOverrides({});
  }

  function updateSelectedBarSettings(patch: Partial<BarProcessingSetting>) {
    if (selectedBar === null) return;
    setBarSettings((current) => {
      const next = { ...current };
      selectedBars.forEach((bar) => {
        const setting = current[bar] ?? { grid, minimumBeats: lengthAssist ? minimumNoteBeats : null, detail: noteDetail };
        next[bar] = { ...setting, ...patch };
      });
      return next;
    });
  }

  function setSelectedBarGrid(value: GridValue) { updateSelectedBarSettings({ grid: value }); }
  function setSelectedBarMinimum(value: number | null) { updateSelectedBarSettings({ minimumBeats: value }); }
  function setSelectedBarDetail(value: number) { updateSelectedBarSettings({ detail: value }); }

  function resetSelectedBarSettings() {
    if (selectedBar === null) return;
    setBarSettings((current) => {
      const next = { ...current };
      selectedBars.forEach((bar) => { delete next[bar]; });
      return next;
    });
  }
  function updateSelectedNoteDuration(deltaBeats: number) {
    if (selectedNoteIndex === null) return;
    const note = notes[selectedNoteIndex];
    if (!note) return;
    const desired = Math.max(0.125, note.durationBeats + deltaBeats);
    setManualDurationOverrides((current) => ({ ...current, [midiNoteKey(note)]: desired }));
    setEditNotice(`${midiToNoteName(note.midi)} の長さを手動調整しました。次の音を越える場合は自動で止まります。`);
  }

  function resetSelectedNoteDuration() {
    if (selectedNoteIndex === null) return;
    const note = notes[selectedNoteIndex];
    if (!note) return;
    setManualDurationOverrides((current) => {
      const next = { ...current };
      delete next[midiNoteKey(note)];
      return next;
    });
    setEditNotice(`${midiToNoteName(note.midi)} の長さを補正結果へ戻しました。`);
  }



  function previewEditorNotes(next: MidiNote[]) {
    setEditedNotes(next);
  }

  function commitEditorNotes(before: MidiNote[], after: MidiNote[] = notes, notice = "タッチ編集を反映しました。UNDOで元に戻せます。") {
    if (JSON.stringify(before) === JSON.stringify(after)) return;
    haltEditorPlayback();
    const taggedAfter = after.map((note, index) => {
      if (JSON.stringify(before[index]) === JSON.stringify(note) || note.source === "VOICE" || note.refinement === "LOCAL_PATTERN") return note;
      return isUnresolvedMidiNote(note)
        ? { ...note, source: "MANUAL" as const }
        : { ...note, pitch: note.midi, status: "RESOLVED" as const, source: "MANUAL" as const };
    });
    const result = recordMidiEdit(before, taggedAfter, { undo: noteUndo, redo: noteRedo });
    setEditedNotes(result.notes);
    setRhythmSlots((current) => current.map((slot) => {
      const note = result.notes.find((item) => item.rhythmSlotId === slot.id);
      const ppq = currentPpq ?? 480;
      return note ? {
        ...slot,
        rawStartTick: note.rawStartTick ?? slot.rawStartTick,
        rawEndTick: note.rawEndTick ?? slot.rawEndTick,
        quantizedStartTick: note.quantizedStartTick ?? slot.quantizedStartTick,
        quantizedEndTick: note.quantizedEndTick ?? slot.quantizedEndTick,
        refinedStartTick: note.refinedStartTick,
        refinedEndTick: note.refinedEndTick,
        refinement: note.refinement,
        startBeat: note.startBeat,
        durationBeats: note.durationBeats,
        startTick: Math.round(note.startBeat * ppq),
        endTick: Math.round((note.startBeat + note.durationBeats) * ppq),
        durationTicks: Math.round(note.durationBeats * ppq),
        pitch: isUnresolvedMidiNote(note) ? null : note.midi,
        status: isUnresolvedMidiNote(note) ? "UNRESOLVED" : "RESOLVED",
        source: note.refinement === "LOCAL_PATTERN"
          ? slot.source
          : note.source === "MANUAL" ? "MANUAL_EDIT" : slot.source,
      } : slot;
    }));
    setNoteUndo(result.history.undo);
    setNoteRedo(result.history.redo);
    setEditNotice(notice);
  }

  function nudgeEditorTime(direction: -1 | 1) {
    const result = nudgeEditableNotes(notes, direction * GRID_BEATS[timeNudgeGrid], {
      selectedNoteIndex,
      barRange: rhythmRefineSelection,
      beatsPerBar,
    });
    if (!result.changedCount) {
      setEditNotice(direction < 0 ? "これ以上前へ移動できません。" : "TIME NUDGEの対象NOTEまたはBARを選択してください。");
      return;
    }
    commitEditorNotes(
      notes.map((note) => ({ ...note })),
      result.notes,
      `TIME NUDGE · ${result.changedCount}音を${Math.abs(result.appliedDeltaBeats)}拍${direction < 0 ? "前" : "後ろ"}へ移動しました。UNDOで戻せます。`,
    );
  }

  function selectRhythmRefineBar(bar: number) {
    const next = advanceBarSelection(rhythmRefineSelection, Math.min(Math.max(0, bar), totalBars - 1));
    setRhythmRefineSelection(next);
    setRhythmRefinePreview(null);
    setSelectedNoteIndex(null);
    const label = next.startBar === next.endBar ? `BAR ${next.startBar + 1}` : `BAR ${next.startBar + 1}–${next.endBar + 1}`;
    setEditNotice(next.awaitingEnd ? `${label}を開始点に選択しました。終了BARをもう一度タップできます。` : `${label}を選択しました。ADD PASSでこの範囲だけ修正できます。`);
  }

  function clearRhythmRefineSelection() {
    setRhythmRefineSelection(null);
    setRhythmRefinePreview(null);
    setEditNotice("BAR選択を解除しました。");
  }

  function changeRhythmRefineStrength(strength: RhythmRefineStrength) {
    setRhythmRefineStrength(strength);
    setRhythmRefinePreview(null);
  }

  function previewSelectedRhythmRefine() {
    if (!rhythmRefineSelection) return;
    const selectedCount = rhythmRefineSelection.endBar - rhythmRefineSelection.startBar + 1;
    haltEditorPlayback();
    const preview = analyzeLocalRhythmRange(notes, {
      startBar: rhythmRefineSelection.startBar,
      barCount: selectedCount,
      beatsPerBar,
      ppq: currentPpq,
      strength: rhythmRefineStrength,
    });
    setRhythmRefinePreview(preview);
    setEditNotice(preview.message);
  }

  function finishRhythmRefine(apply: boolean) {
    const preview = rhythmRefinePreview;
    setRhythmRefinePreview(null);
    if (!apply || !preview?.shouldRefine || !preview.hasChanges) {
      setEditNotice(apply ? preview?.message ?? "補正候補はありません。" : "リズム補正Previewを取り消しました。RAWは変更していません。");
      return;
    }
    const label = preview.range.barCount === 1
      ? "BAR " + (preview.range.startBar + 1)
      : "BAR " + (preview.range.startBar + 1) + "–" + (preview.range.startBar + preview.range.barCount);
    commitEditorNotes(
      notes.map((note) => ({ ...note })),
      preview.refinedNotes,
      label + " の局所リズム補正を適用しました。UNDOでRAW timingへ戻せます。",
    );
  }


  function undoEditor() {
    haltEditorPlayback();
    const result = undoMidiEdit(notes, { undo: noteUndo, redo: noteRedo });
    if (!result) return;
    setEditedNotes(result.notes);
    setNoteUndo(result.history.undo);
    setNoteRedo(result.history.redo);
    setSelectedNoteIndex(null);
    setEditNotice("編集を1つ戻しました。");
  }

  function redoEditor() {
    haltEditorPlayback();
    const result = redoMidiEdit(notes, { undo: noteUndo, redo: noteRedo });
    if (!result) return;
    setEditedNotes(result.notes);
    setNoteUndo(result.history.undo);
    setNoteRedo(result.history.redo);
    setSelectedNoteIndex(null);
    setEditNotice("編集を1つやり直しました。");
  }

  function deleteEditorNote() {
    if (selectedNoteIndex === null || !notes[selectedNoteIndex]) return;
    const before = notes.map((note) => ({ ...note }));
    const after = notes.filter((_, index) => index !== selectedNoteIndex);
    commitEditorNotes(before, after, "選択したノートを削除しました。");
    setSelectedNoteIndex(null);
  }
  function changeTask(next: TaskMode) {
    engineRef.current?.stopAllAudio();
    clearPlaybackTimers();
    setIsPlaying(false);
    setPlaybackLabel("");
    if (next !== "edit" && editModeRef.current === "voice") leaveVoiceDrawMode("select");
    if (next !== "capture" && next !== "piano" && isInputTesting) stopInputTest();
    if (next === "capture" && stage !== "recording" && taskMode !== "capture") {
      clearPassCaptureState();
      setRhythmEvidenceMode(null);
      setRhythmPickupEnabled(false);
      publishCaptureSession(createNewCaptureSession({
        sessionId: nextCaptureSessionId(),
        inputMethod: captureSegmentationMode === "rhythm-guided" ? "BOTH" : "VOICE",
        tempo: bpm,
        grid,
      }));
    }
    if (next === "capture") {
      setCaptureWorkspaceActive(false);
      setCaptureDetailsOpen(false);
      setHomeTool(null);
    }
    setAddPassOpen(false);
    setGuideOpen(false);
    setTaskMode(next);
    setNavOpen(false);
  }
  async function holdTones(key: string, midis: number[]) {
    stopTones(key);
    const audio = await ensureWorkspaceAudio("screen-keyboard");
    if (!audio) return;
    heldKeysRef.current.add(key);
    const voiceIds = await Promise.all(midis.map((midi) => audio.startSustainedTone(midi, midis.length > 1 ? 0.075 : 0.12)));
    if (!heldKeysRef.current.has(key)) {
      voiceIds.forEach((voiceId) => audio.stopSustainedTone(voiceId));
      return;
    }
    heldVoicesRef.current.set(key, voiceIds);
  }

  function stopTones(key: string) {
    heldKeysRef.current.delete(key);
    heldVoicesRef.current.get(key)?.forEach((voiceId) => engine().stopSustainedTone(voiceId));
    heldVoicesRef.current.delete(key);
  }

  function canPlayMidi(midi: number): boolean {
    return !(scaleLock && selectedKey && !scale.has(midi % 12));
  }

  function keyHoldProps(key: string, midis: number[]) {
    return {
      onPointerDown: (event: React.PointerEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        void holdTones(key, midis);
      },
      onPointerUp: () => stopTones(key),
      onPointerCancel: () => stopTones(key),
      onLostPointerCapture: () => stopTones(key),
      onContextMenu: (event: React.MouseEvent<HTMLButtonElement>) => event.preventDefault(),
    };
  }

  const keyLabel = selectedKey ? `${selectedKey.tonic} ${selectedKey.mode === "major" ? "Major" : "Minor"}` : "—";
  const assistedKeyLabel = currentKeySegment ? `${currentKeySegment.key.tonic} ${currentKeySegment.key.mode === "major" ? "Major" : "Minor"} · 信頼度 ${keyConfidenceLabel(currentKeySegment.confidence)}` : "履歴を解析中";
  const sensitivityLabel = micSensitivity >= 75 ? "HIGH" : micSensitivity >= 45 ? "NORMAL" : "CLEAN";
  const inputLevelPercent = Math.min(100, Math.round(((currentFrame?.level ?? 0) / 0.08) * 100));
  const calibrationPercent = Math.min(100, Math.round(((noiseCalibration?.p90 ?? 0) / 0.08) * 100));
  const calibrationStale = Boolean(noiseCalibration && !activeCalibration);
  const calibrationQuality = noiseCalibration?.quality === "retake" ? "取り直し推奨" : noiseCalibration?.quality === "usable" ? "使用可能" : "静か";
  const minimumNoteLabel = minimumNoteBeats === 1 ? "1/4" : minimumNoteBeats === 0.5 ? "1/8" : minimumNoteBeats === 0.25 ? "1/16" : "1/32";
  const voiceMapStale = Boolean(voiceProfile && !activeVoiceProfile);
  const weakVoiceSamples = voiceProfile?.samples.filter((sample) => sample.weak).length ?? 0;
  const mapImpact = voiceProfile ? voiceMapImpact(voiceProfile, micProfile.rmsThreshold, micProfile.confidenceThreshold) : null;
  const selectedNote = selectedNoteIndex === null ? null : notes[selectedNoteIndex] ?? null;
  const selectedNoteGrid = selectedNote ? (barSettings[Math.floor(selectedNote.startBeat / 4)]?.grid ?? grid) : grid;
  const selectedNoteStep = GRID_BEATS[selectedNoteGrid];
  const manualEditCount = Object.keys(manualDurationOverrides).length;
  const chordPads = selectedKey ? (showAllChords
    ? (selectedKey.mode === "major" ? ["I", "ii", "iii", "IV", "V", "vi", "vii°"] : ["i", "ii°", "III", "iv", "v", "VI", "VII"]).map((label, degree) => ({ degree, label }))
    : (selectedKey.mode === "major" ? [{ degree: 0, label: "I" }, { degree: 3, label: "IV" }, { degree: 4, label: "V" }, { degree: 5, label: "vi" }] : [{ degree: 0, label: "i" }, { degree: 3, label: "iv" }, { degree: 4, label: "v" }, { degree: 5, label: "VI" }])) : [];
  const selectedBarSetting = selectedBar === null ? null : { grid: barSettings[selectedBar]?.grid ?? grid, minimumBeats: barSettings[selectedBar] ? barSettings[selectedBar].minimumBeats : (lengthAssist ? minimumNoteBeats : null), detail: barSettings[selectedBar]?.detail ?? noteDetail };
  const selectedRangeLabel = selectedBar === null ? "" : activeSelectedBarCount === 1 ? `BAR ${selectedBar + 1}` : `BAR ${selectedBar + 1}–${selectedBar + activeSelectedBarCount}`;
  const redoRangeLabel = redoRange ? redoRange.count === 1 ? `BAR ${redoRange.start + 1}` : `BAR ${redoRange.start + 1}–${redoRange.start + redoRange.count}` : "";
  const captureHome = taskMode === "capture" && !captureWorkspaceActive && !rhythmEvidenceMode;
  const rhythmInputActive = taskMode === "capture" && !redoRange && (stage === "counting" || stage === "recording") && (captureSegmentationMode === "rhythm-guided" || activePassDraft?.inputMethod === "BOTH");

  return (
    <main className={`studio-shell task-${taskMode} stage-${stage}${captureHome ? " capture-home" : ""}${homeTool ? " home-tool-" + homeTool : ""}${captureDetailsOpen ? " capture-details-open" : ""}${stage === "result" ? " capture-result" : ""}${rhythmInputActive ? " rhythm-input-active" : ""}`}>
      <PwaUpdateBanner />
      <header className="studio-header">
        <div><p className="product-kicker">P-013 · MULTITRACK</p><h1>鼻歌をMIDIへ。</h1></div>
        <div className="privacy-chip"><span /> 端末内処理</div>
      </header>

      <button type="button" className="task-menu-trigger" aria-expanded={navOpen} aria-controls="workspace-drawer" onClick={() => setNavOpen((current) => !current)}>
        <span>☰</span><small>MENU</small>
      </button>
      {navOpen && <button type="button" className="task-nav-backdrop" aria-label="メニューを閉じる" onClick={() => setNavOpen(false)} />}
      <aside id="workspace-drawer" className="task-drawer" role="dialog" aria-modal={navOpen ? "true" : undefined} aria-label="作業モード" hidden={!navOpen}>
        <div className="task-drawer-head"><div><span>WORKSPACE</span><strong>必要な機能を開く</strong></div><button type="button" onClick={() => setNavOpen(false)} aria-label="メニューを閉じる">×</button></div>
        <nav aria-label="作業モード">
          {TASK_OPTIONS.map((option) => (
            <button type="button" key={option.value} className={taskMode === option.value ? "active" : ""} onClick={() => changeTask(option.value)}>
              <span>{option.icon}</span><strong>{option.label}</strong><small>{option.description}</small>
            </button>
          ))}
        </nav>
        <button type="button" className="how-it-works-trigger" onClick={() => { setNavOpen(false); setHowItWorksOpen(true); }}><span>?</span><strong>HOW IT WORKS</strong><small>鼻歌→MIDIの仕組み</small></button>
      </aside>
      <HowItWorksPanel open={howItWorksOpen} onClose={() => setHowItWorksOpen(false)} />
      <ConciergePanel
        open={conciergeOpen}
        phase={conciergePhase}
        bpm={bpm}
        grid={grid}
        tapCount={tapCount}
        count={count}
        proposal={conciergeProposal}
        beforeCount={conciergeBefore?.noteCount ?? null}
        afterCount={notes.length}
        melodyTapRecommendationApplied={melodyTapUndo !== null}
        onClose={() => { stopMelodyTapAudio(); setConciergeOpen(false); }}
        onChooseTempo={(value) => { setBpm(value); tapTimesRef.current = []; setTapCount(0); }}
        onGridChange={setGrid}
        onTap={() => void tapTempo()}
        onIssue={chooseConciergeIssue}
        onAnalyze={reanalyzeWithConcierge}
        onAdopt={adoptConcierge}
        onRevert={revertConcierge}
        onStartMelodyTapAudio={startMelodyTapAudio}
        onStopMelodyTapAudio={stopMelodyTapAudio}
        onApplyMelodyTap={applyMelodyTapRecommendation}
        onRestoreMelodyTap={restoreMelodyTapRecommendation}
      />

      {addPassOpen && <>
        <button type="button" className="concierge-backdrop" aria-label="ADD PASSを閉じる" onClick={() => setAddPassOpen(false)} />
        <aside className="concierge-panel add-pass-dialog" role="dialog" aria-modal="true" aria-label="続きを追加">
          <AddPassPanel
            nextBar={nextPassBar}
            selectedRange={selectedInputRange()}
            beatsPerBar={beatsPerBar}
            bpm={bpm}
            hasNotesInRange={(startBar, endBar) => {
              const length = endBar === null ? null : endBar - startBar + 1;
              const range = passRangeTicks(startBar, length, currentTimeSignature, currentPpq);
              return regionHasNotes(notes, range.startTick, range.endTick, currentPpq);
            }}
            onStart={beginInputPass}
            onCancel={() => setAddPassOpen(false)}
          />
        </aside>
      </>}
      {rhythmEvidenceMode && <>
        <button type="button" className="concierge-backdrop" aria-label="譜割り入力を閉じる" onClick={closeRhythmEvidence} />
        <aside className="concierge-panel rhythm-evidence-panel" role="dialog" aria-modal="true" aria-label={rhythmEvidenceMode === "rhythm-refine" ? "譜割りを整える" : "リズムから作る"}>
          <header><div><span>2-PASS INPUT</span><strong>{rhythmEvidenceMode === "rhythm-refine" ? "譜割りを整える" : "リズムから作る"}</strong></div><button type="button" onClick={closeRhythmEvidence} aria-label="閉じる">×</button></header>
          {rhythmEvidenceMode === "rhythm-refine" && (
            <section className="audio-alignment-panel" aria-label="声とクリックの位置補正">
              <div><span>AUDIO ALIGNMENT</span><strong>声とクリックを先に合わせる</strong><small>端末差は自動推測せず、この録音だけを非破壊で前後します。MIDIのTIME NUDGEとは別です。</small></div>
              <output>{audioAlignmentMs > 0 ? "+" : ""}{audioAlignmentMs} ms</output>
              <div className="audio-alignment-actions">
                <button type="button" onClick={() => adjustAudioAlignment(-50)}>声を早く −50</button>
                <button type="button" onClick={() => adjustAudioAlignment(-10)}>−10</button>
                <button type="button" onClick={() => adjustAudioAlignment("reset")}>RESET</button>
                <button type="button" onClick={() => adjustAudioAlignment(10)}>＋10</button>
                <button type="button" onClick={() => adjustAudioAlignment(50)}>声を遅く ＋50</button>
              </div>
              <small>AUTO: 利用不可（この端末の実測基準信号がないため） · 下の開始後、声とクリックを聴いて戻り、必要なら調整します。</small>
            </section>
          )}
          <MelodyTapGuide
            key={rhythmEvidenceMode}
            bpm={bpm}
            count={count}
            tapCount={tapCount}
            onTapTempo={() => void tapTempo()}
            onBpmChange={(value) => { setBpm(value); tapTimesRef.current = []; setTapCount(0); }}
            bpmConfirmed
            recommendationApplied={false}
            purpose={rhythmEvidenceMode}
            onStartAudio={startRhythmEvidenceAudio}
            onStopAudio={stopRhythmEvidenceAudio}
            onApply={() => undefined}
            onRestore={() => undefined}
            onCreate={createNotesFromRhythmEvidence}
            pickupEnabled={activePassDraft?.pickup ?? rhythmPickupEnabled}
            onPickupChange={(enabled) => {
              setRhythmPickupEnabled(enabled);
              if (activePassDraft) {
                const nextDraft = { ...activePassDraft, pickup: enabled };
                activePassDraftRef.current = nextDraft;
                setActivePassDraft(nextDraft);
              }
            }}
            countInBeats={!activePassDraft || activePassDraft.preRoll === "COUNT_IN" ? 4 : Math.max(1, Math.round(beatsPerBar * preRollBarCount(activePassDraft.preRoll)))}
            targetLabel={activePassDraft ? passTargetLabel(activePassDraft) : "SONG START"}
            onBack={closeRhythmEvidence}
          />
        </aside>
      </>}

      {guideOpen && <button type="button" className="guide-panel-backdrop" aria-label="GUIDEを閉じる" onClick={() => setGuideOpen(false)} />}
      {guideOpen && (
        <aside className="capture-guide-panel" role="dialog" aria-modal="true" aria-label="録音メロディーのKeyとScale">
          <div className="capture-guide-head">
            <div><span>GUIDE / ANALYZE</span><strong>録音したメロディーのKeyとScale</strong></div>
            <button type="button" onClick={() => setGuideOpen(false)} aria-label="GUIDEを閉じる">×</button>
          </div>
          <div className="capture-guide-summary">
            <span>SELECTED KEY</span><strong>{keyLabel}</strong>
            <small>{selectedKey ? Array.from(scale).map((pitchClass) => NOTE_NAMES[pitchClass]).join(" · ") : "CAPTURE後に候補を選んでください"}</small>
          </div>
          <div className="capture-guide-keys">
            {keys.length ? keys.map((candidate) => (
              <button key={candidate.tonic + candidate.mode} className={selectedKey === candidate ? "selected" : ""} onClick={() => { setSelectedKey(candidate); setReferenceMidi(60 + candidate.tonicIndex); }}>
                <span>{candidate.tonic} {candidate.mode === "major" ? "Major" : "Minor"}</span><strong>{candidate.confidence}%</strong>
              </button>
            )) : <p>録音後にKey候補が表示されます。</p>}
          </div>
          {selectedKey && (
            <div className="capture-guide-chords">
              <label><input type="checkbox" checked={seventh} onChange={(event) => setSeventh(event.target.checked)} /> 7TH</label>
              <div>{chordPads.map(({ degree, label }) => {
                const chord = buildChord(selectedKey.tonicIndex, selectedKey.mode, degree, seventh, 48);
                return <button key={label} {...keyHoldProps("guide-chord-" + label, chord)}><strong>{label}{seventh ? "7" : ""}</strong><small>{chord.map(midiToNoteName).join(" · ")}</small></button>;
              })}</div>
            </div>
          )}
          <div className="guide-scale-keyboard" aria-label="推定Scaleの簡易鍵盤">
            {KEYBOARD_NOTES.slice(12, 25).map((midi) => {
              const inScale = !selectedKey || scale.has(midi % 12);
              return <button key={midi} className={(BLACK.has(midi % 12) ? "black " : "white ") + (inScale ? "in-scale" : "out-scale")} {...keyHoldProps("guide-key-" + midi, [midi])}><span>{midiToNoteName(midi)}</span></button>;
            })}
          </div>
        </aside>
      )}

      {taskMode === "piano" && (
        <PianoPositionMode
          voiceFrame={inputTestFrame}
          captureKeyCandidates={keys}
          liveKey={livePianoKey}
          liveKeySource={livePianoKeySource}
          keyGuideEnabled={livePianoKeyGuide}
          harmonyMode={livePianoHarmony}
          onSelectLiveKey={(candidate, source) => {
            setLivePianoKey(candidate);
            setLivePianoKeySource(source);
            if (!candidate) {
              setLivePianoKeyGuide(false);
              setLivePianoHarmony("off");
            }
          }}
          onKeyGuideEnabled={setLivePianoKeyGuide}
          onHarmonyMode={setLivePianoHarmony}
          isVoiceListening={isInputTesting}
          onStartVoice={() => void startInputTest()}
          onStopVoice={stopInputTest}
          onNoteStart={async (noteNumber, key) => {
            await holdTones(key, [noteNumber]);
            return engine().getContextState();
          }}
          onNoteStop={stopTones}
        />
      )}

      {taskMode === "edit" && (
        <MobilePianoRollEditor
          key={editorMountKey}
          notes={notes}
          gridStep={GRID_BEATS[grid]}
          gridLabel={grid}
          timeNudgeGrid={timeNudgeGrid}
          editMode={editMode}
          previewInstrument={previewInstrument}
          zoom={horizontalZoom}
          selectedNoteIndex={selectedNoteIndex}
          canUndo={noteUndo.length > 0}
          canRedo={noteRedo.length > 0}
          isPlaying={isPlaying}
          playheadBeat={playheadBeat}
          voiceDrawState={voiceDrawState}
          timeSignature={currentTimeSignature}
          midiImport={midiImport ? {
            fileName: midiImport.fileName,
            ppq: midiImport.timing.ppq,
            bpm: midiImport.bpm,
            selectedTrackIndex: midiImport.selectedTrackIndex,
            tracks: midiImport.tracks.map((track) => ({ index: track.index, name: track.name, noteCount: track.notes.length })),
          } : null}
          nextBarNumber={nextPassBar + 1}
          rhythmRefineSelection={rhythmRefineSelection}
          rhythmRefineStrength={rhythmRefineStrength}
          rhythmRefinePreview={rhythmRefinePreview}
          onSelectRhythmRefineBar={selectRhythmRefineBar}
          onClearRhythmRefineSelection={clearRhythmRefineSelection}
          onRhythmRefineStrengthChange={changeRhythmRefineStrength}
          onPreviewRhythmRefine={previewSelectedRhythmRefine}
          onApplyRhythmRefine={finishRhythmRefine}
          onEditModeChange={changeEditorMode}
          onVoiceGestureStart={startVoiceDrawGesture}
          onVoiceGestureMove={moveVoiceDrawGesture}
          onVoiceGestureEnd={finishVoiceDrawGesture}
          onVoiceGestureCancel={cancelActiveVoiceDraw}
          onPreviewInstrumentChange={(instrument) => {
            if (isPlaying) haltEditorPlayback();
            setPreviewInstrument(instrument);
            setEditNotice(instrument === "piano" ? "プレビュー音をPIANOへ変更しました。" : "プレビュー音をHOLDへ変更しました。");
          }}
          onSelectNote={setSelectedNoteIndex}
          onPreviewNotes={previewEditorNotes}
          onCommitNotes={commitEditorNotes}
          onUndo={undoEditor}
          onRedo={redoEditor}
          onDelete={deleteEditorNote}
          onTimeNudge={nudgeEditorTime}
          onTimeNudgeGridChange={setTimeNudgeGrid}
          onZoomChange={setHorizontalZoom}
          onPlay={() => void playConvertedMidi()}
          onPause={pauseEditorPlayback}
          onStop={stopEditorPlayback}
          onGoToStart={goToEditorStart}
          onPreviousBar={previousEditorBar}
          onExport={exportMidi}
          onOpenMidi={() => void openMidiForEditing()}
          onImportedTrackChange={(trackIndex) => midiImport && applyImportedTrack(midiImport, trackIndex)}
          onOpenMenu={() => setNavOpen(true)}
          onAddPass={() => setAddPassOpen(true)}
        />
      )}

      <nav className="session-flow" aria-label="現在の作業段階">
        <span className={noiseCalibration ? "done" : "active"}>1 環境</span><span className={voiceProfile ? "done" : activeCalibration ? "active" : ""}>2 声</span><span className={frames.length ? "done" : stage === "recording" ? "active" : ""}>3 録音</span><span className={selectedBar !== null ? "active" : stage === "result" ? "done" : ""}>4 部分編集</span><span className={tracks.length || chordPlan.length ? "active" : ""}>5 伴奏・出力</span>
      </nav>

      {captureHome && (
        <section className="home-action-hub" aria-label="したいことを選ぶ">
          <header><span>P-013 / MULTITRACK VOCAL MIDI</span><strong>何をしますか？</strong><small>P-010とは別の次世代マルチトラック版です</small></header>
          <div className="home-action-group primary">
            <span>録音する</span>
            <button type="button" onClick={() => { setCaptureSegmentationMode("auto"); setCaptureWorkspaceActive(true); setHomeTool(null); }}><b>声を録音</b><small>声から音程と譜割りを作る</small></button>
            <button type="button" onClick={() => { setCaptureSegmentationMode("rhythm-guided"); setCaptureWorkspaceActive(true); setHomeTool(null); }}><b>声＋タップ</b><small>歌いながら区切りを入れる</small></button>
            <button type="button" onClick={() => openRhythmEvidence("rhythm-first")}><b>リズムから作る</b><small>Tap / Holdの後で声を付ける</small></button>
          </div>
          <div className="home-action-group edit-actions">
            <span>続ける・直す</span>
            <button type="button" disabled={!notes.length} onClick={() => changeTask("edit")}><b>メロディーを編集</b><small>{notes.length ? notes.length + " notes を開く" : "録音またはMIDIを先に用意"}</small></button>
            <button type="button" onClick={() => setAddPassOpen(true)}><b>続きを追加</b><small>聴き始め・録音開始・終了を決める</small></button>
            <button type="button" disabled={!frames.length || !voiceUrl} onClick={() => openRhythmEvidence("rhythm-refine")}><b>譜割りを整える</b><small>声とクリックを合わせてからタップ</small></button>
          </div>
          <div className="home-mini-tools" aria-label="その他の機能">
            <button type="button" className={homeTool === "pitch" ? "active" : ""} onClick={() => setHomeTool((current) => current === "pitch" ? null : "pitch")}>♪<small>音程テスト</small></button>
            <button type="button" className={homeTool === "tempo" ? "active" : ""} onClick={() => setHomeTool((current) => current === "tempo" ? null : "tempo")}>♩<small>BPM / GRID</small></button>
            <button type="button" onClick={() => void openMidiForEditing()}>⇧<small>MIDIを開く</small></button>
            <button type="button" onClick={() => changeTask("settings")}>⚙<small>設定</small></button>
          </div>
          {homeTool === "tempo" && <div className="home-tool-sheet" aria-label="BPMとGRID">
            <label><span>BPM</span><strong>{bpm}</strong><input aria-label="HOME BPM" type="range" min="40" max="240" value={bpm} onChange={(event) => setBpm(Number(event.target.value))} /></label>
            <button type="button" onClick={() => void tapTempo()}>TAP<small>{tapCount > 1 ? bpm + " BPM" : "2回以上タップ"}</small></button>
            <div><span>GRID</span>{(["1/4", "1/8", "1/16", "1/32"] as GridValue[]).map((value) => <button type="button" key={value} className={grid === value ? "active" : ""} onClick={() => setGrid(value)}>{value}</button>)}</div>
          </div>}
        </section>
      )}

      <section className="transport" aria-label="録音コントロール">
        <div className="transport-copy">
          <span className="status-label">{stage === "stopping" ? "STOPPING · 録音を確定中" : stage === "recording" ? redoRange ? `RETAKE · ${redoRangeLabel}` : captureSegmentationMode === "rhythm-guided" ? `RHYTHM GUIDED · ${rhythmGestures.length + (activeRhythmStart !== null ? 1 : 0)} BOUNDARIES` : `RECORDING · LAYER ${tracks.length + 1}` : isPlaying ? `PLAYING · ${playbackLabel}` : stage === "result" ? "CONVERTED TAKE · BARをタップして範囲編集" : tracks.length ? `READY · ${tracks.length} TRACKS` : "NEW SESSION"}</span>
          <strong>{formatTime(duration)}</strong>
          <small>{conversionMode === "key-assist" ? `KEY ASSIST · ${assistedKeyLabel}` : `STANDARD · KEY ${keyLabel}`} · {captureSegmentationMode === "rhythm-guided" ? "TAP GUIDED" : "AUTO RHYTHM"} · {bpm} BPM · {activeTracks.length} ACTIVE TRACKS</small>
          {taskMode === "capture" && stage !== "result" && (
            <div className="capture-session-summary" aria-label="今回のCapture設定">
              <span>CAPTURE SESSION · {captureSession?.sessionId ?? "NEW"}</span>
              <b>{captureSession ? captureSessionSummary(captureSession, currentPpq, currentTimeSignature) : `4拍カウント → BAR 1から${captureSegmentationMode === "rhythm-guided" ? "TAP" : "録音"}開始`}</b>
              <small>{captureSession?.status ?? "SETUP"} · {bpm} BPM · GRID {grid} · 前回のADD PASS設定は引き継ぎません</small>
            </div>
          )}
        </div>
        <div className="transport-actions">
          {taskMode === "capture" && stage !== "recording" && <button className="concierge-trigger" onClick={() => openConcierge(stage === "result" ? "after" : "before")}><span>{stage === "result" ? "調整" : "録音ガイド"}</span><small>{stage === "result" ? "困りごとから再解析" : "BPM / MELODY TAP"}</small></button>}
          {isPlaying && <button className="global-stop" onClick={stopPlayback}>■ STOP</button>}
          {stage === "recording" || stage === "stopping"
            ? <button className="record-button is-recording" onClick={stopRecording} disabled={stage === "stopping"} aria-label={stage === "stopping" ? "録音を停止中" : "録音を停止"}><span /></button>
            : stage === "preparing" || stage === "counting"
              ? <button className="capture-cancel-button" type="button" onClick={cancelCapturePreparation}>CANCEL<small>録音準備を中止</small></button>
              : <button className="record-button" onClick={startRecording} disabled={isCalibrating || isVoiceMapping || isInputTesting} aria-label="新しいMIDIトラックを録音"><span /></button>}
        </div>
      </section>

      {!rhythmEvidenceMode && (stage === "preparing" || (stage === "counting" && !(taskMode === "capture" && !redoRange && (captureSegmentationMode === "rhythm-guided" || activePassDraft?.inputMethod === "BOTH")))) && (
        <section className="count-panel" role="status">
          {stage === "preparing" ? <div className="prepare-lines"><Skeleton count={2} baseColor="#1d2026" highlightColor="#30343c" /></div> : <strong>{count}</strong>}
          <span>{stage === "preparing" ? "マイクを準備中" : overdubMonitoringEnabled && monitorMidiTracks.length ? "COUNT IN · REC MON ARMED" : "COUNT IN"}</span>
        </section>
      )}
      {error && <p className="error-message" role="alert">{error}</p>}

      <section className="live-strip" aria-hidden={taskMode === "capture" && captureHome && !isInputTesting}>
        <div><span>LIVE PITCH</span><strong>{currentFrame?.midiFloat !== null && currentFrame?.midiFloat !== undefined ? midiToNoteName(currentFrame.midiFloat) : "—"}</strong></div>
        <div><span>FREQUENCY</span><strong>{currentFrame?.frequency ? `${currentFrame.frequency.toFixed(1)} Hz` : "—"}</strong></div>
        <div className="level-cell"><span>MIC LEVEL</span><div className="level-meter"><i style={{ width: `${inputLevelPercent}%` }} /></div><strong>{inputLevelPercent}%</strong></div>
        <div><span>GRID</span><strong>{grid}</strong></div>
      </section>

      {taskMode === "capture" && !captureHome && stage !== "recording" && stage !== "preparing" && stage !== "counting" && (
        <section className="capture-essential-controls" aria-label="録音方法">
          <div><span>録音方法</span><button type="button" className={captureSegmentationMode === "auto" ? "active" : ""} onClick={() => setCaptureSegmentationMode("auto")}><strong>声から自動</strong></button><button type="button" className={captureSegmentationMode === "rhythm-guided" ? "active" : ""} onClick={() => setCaptureSegmentationMode("rhythm-guided")}><strong>声＋タップ</strong></button></div>
          <button ref={captureDetailsTriggerRef} type="button" className="capture-details-trigger" aria-expanded={captureDetailsOpen} aria-controls="capture-settings-popover" onClick={() => setCaptureDetailsOpen((current) => !current)}>⚙<small>BPM {bpm} · GRID {grid}</small></button>
        </section>
      )}

      {taskMode === "capture" && (stage === "recording" || stage === "counting") && !redoRange && (captureSegmentationMode === "rhythm-guided" || activePassDraft?.inputMethod === "BOTH") && (
        <RhythmGuidedPad
          elapsed={elapsed}
          gestures={rhythmGestures}
          activeStartTime={activeRhythmStart}
          armed={stage === "recording" || (stage === "counting" && Boolean(activePassDraft?.pickup))}
          countdown={stage === "counting" ? count : null}
          onStart={startRhythmGesture}
          onEnd={(pointerId) => { finishRhythmGesture(pointerId); }}
        />
      )}
      {taskMode === "capture" && stage !== "recording" && stage !== "stopping" && stage !== "preparing" && stage !== "counting" && (
        <AnchoredPopover id="capture-settings-popover" open={captureDetailsOpen} triggerRef={captureDetailsTriggerRef} onClose={() => setCaptureDetailsOpen(false)} className="capture-settings-popover">
        <section className="capture-quick-controls" aria-label="録音前の主要設定">
          <div className="capture-method-toggle"><span>譜割り</span><button type="button" className={captureSegmentationMode === "auto" ? "active" : ""} onClick={() => { setCaptureSegmentationMode("auto"); if (stage === "result" && rhythmSession) setEditNotice("同じPitch履歴をAUTOの自動譜割りで再解析しました。タップで譜割りへ戻して比較できます。"); }} disabled={editedNotes !== null}><strong>自動</strong><small>声から区切る</small></button><button type="button" className={captureSegmentationMode === "rhythm-guided" ? "active" : ""} onClick={() => { setCaptureSegmentationMode("rhythm-guided"); if (stage === "result" && rhythmSession) setEditNotice("保存したtap / hold境界でRHYTHM GUIDEDを再解析しました。"); }} disabled={editedNotes !== null}><strong>タップで譜割り</strong><small>歌いながら区切る</small></button>{stage === "result" && rhythmSession && <small>同じ録音を切替比較できます</small>}</div>
          <label className="capture-bpm-control"><span>BPM</span><strong>{bpm}</strong><BpmNumberInput bpm={bpm} onChange={(value) => { setBpm(value); tapTimesRef.current = []; setTapCount(0); }} ariaLabel="CAPTURE BPM数値" /><input aria-label="CAPTURE BPMスライダー" type="range" min="40" max="240" value={bpm} onChange={(event) => setBpm(Number(event.target.value))} /></label>
          <label className={"capture-output-volume " + (outputVolume === 0 ? "muted" : "")}><span>{outputVolume === 0 ? "🔇 MUTED" : "🔊 VOL"}</span><strong>{outputVolume}%</strong><input aria-label="CAPTURE アプリ出力音量" type="range" min="0" max="100" step="1" value={outputVolume} onChange={(event) => changeOutputVolume(Number(event.target.value))} />{outputVolume === 0 && <small>アプリ音量が0です</small>}</label>
          <button type="button" className="capture-tap" onClick={() => void tapTempo()}>TAP<small>{tapCount > 1 ? bpm + " BPM" : "テンポ"}</small></button>
          <div className="capture-grid"><span>GRID</span>{(["1/4", "1/8", "1/16", "1/32"] as GridValue[]).map((value) => <button key={value} className={grid === value ? "active" : ""} onClick={() => setGrid(value)}>{value}</button>)}</div>
          <button type="button" className="capture-guide-trigger" onClick={() => { setCaptureDetailsOpen(false); setGuideOpen(true); }}>GUIDE<small>KEY / SCALE</small></button>
        </section>
        </AnchoredPopover>
      )}
      {taskMode === "capture" && stage !== "recording" && stage !== "preparing" && stage !== "counting" && (
        <details className="capture-result-actions">
          <summary>{stage === "result" ? "録音後の操作" : "追加の録音方法"}</summary>
          <section className="rhythm-evidence-actions" aria-label="2-pass入力">
          <button type="button" className="add-pass-action" onClick={() => setAddPassOpen(true)}><span>REGION</span><strong>続きを追加</strong><small>VOICE / RHYTHM / VOICE + TAP</small></button>
          {stage === "result" && frames.length > 0 && voiceUrl && <button type="button" className="rhythm-refine-action" onClick={() => openRhythmEvidence("rhythm-refine")}><span>REFINE</span><strong>譜割りを整える</strong><small>元音声＋保存済みF0を使い、Tap/Holdで区切りだけ取り直す</small></button>}
          </section>
        </details>
      )}
      {stage === "result" && notes.length > 0 && taskMode === "capture" && (
        <button className="capture-next" onClick={() => changeTask("edit")}>録音できました <strong>EDITで形を整える →</strong></button>
      )}

      <section className="roll-card arrange-card">
        <div className="roll-head"><span>ARRANGE VIEW · CHORD / MELODY / TRACKS</span><span>{notes.length} NOTES · {totalBars} BARS</span></div>
        <div className="arrange-toolbar">
          <div className="zoom-presets" aria-label="横倍率">{[0.5, 1, 2, 4].map((value) => <button key={value} className={Math.abs(horizontalZoom - value) < 0.001 ? "active" : ""} onClick={() => setHorizontalZoom(value)}>{value.toFixed(1)}×</button>)}</div>
          <small>0.5〜4.0倍 · 横スクロール · 音域は歌った範囲へ自動調整</small>
        </div>
        <PitchRoll frames={frames} notes={notes} bpm={bpm} duration={duration} confidenceThreshold={micProfile.confidenceThreshold} chords={chordPlan.map((item) => ({ bar: item.bar, label: `${item.label}${effectiveSeventh ? "7" : ""}`, warning: item.warning }))} transitions={chordTransitions} tracks={timelineTracks} horizontalZoom={horizontalZoom} playheadBeat={playheadBeat} selectedBar={selectedBar} selectedBarCount={activeSelectedBarCount} selectedNoteIndex={selectedNoteIndex} onSelectNote={setSelectedNoteIndex} onSelectBar={(bar) => { const nextBar = Math.min(bar, totalBars - 1); setSelectedBar(nextBar); setSelectedBarCount((current) => Math.max(1, Math.min(current, totalBars - nextBar))); }} />
        {!frames.length && <div className="roll-empty">RECを押して、カウントの後に鼻歌を歌ってください</div>}
        {stage === "result" && selectedBarSetting && <div className="bar-editor">
          <div className="selection-summary"><span>SELECTED RANGE</span><strong>{selectedRangeLabel}</strong><small>紫の範囲だけ解析・歌い直し</small></div>
          <label className="bar-range-control"><span>RANGE</span><div><strong>{activeSelectedBarCount}</strong><em>BARS</em></div><input aria-label="歌い直すBar数" type="range" min="1" max={Math.min(8, totalBars - selectedBar!)} step="1" value={activeSelectedBarCount} onChange={(event) => setSelectedBarCount(Number(event.target.value))} /></label>
          <label><span>GRID</span><div className="segmented">{(["1/4", "1/8", "1/16", "1/32"] as GridValue[]).map((value) => <button key={value} className={selectedBarSetting.grid === value ? "active" : ""} onClick={() => setSelectedBarGrid(value)}>{value}</button>)}</div></label>
          <label className="bar-detail-control"><span>NOTE DETAIL · {noteDetailProfile(selectedBarSetting.detail).label}</span><input aria-label="選択範囲の音の細かさ" type="range" min="0" max="100" step="5" value={selectedBarSetting.detail} onChange={(event) => setSelectedBarDetail(Number(event.target.value))} /><small>高いほど短い音・同音の再アタックを残す</small></label>
          <label><span>MIN NOTE</span><div className="segmented"><button className={selectedBarSetting.minimumBeats === null ? "active" : ""} onClick={() => setSelectedBarMinimum(null)}>OFF</button>{[{ value: .125, label: "1/32" }, { value: .25, label: "1/16" }, { value: .5, label: "1/8" }, { value: 1, label: "1/4" }].map((option) => <button key={option.label} className={selectedBarSetting.minimumBeats === option.value ? "active" : ""} onClick={() => setSelectedBarMinimum(option.value)}>{option.label}</button>)}</div></label>
          <div className="bar-editor-actions"><button onClick={resetSelectedBarSettings}>全体設定へ戻す</button><button className="retake-button" onClick={() => void startBarRedo()}><span className="rec-mark"><i /> REC</span><em>{selectedRangeLabel} を歌い直す</em></button></div>
        </div>}
        {editNotice && <p className="edit-notice">{editNotice}</p>}
        {stage === "result" && notes.length > 0 && <div className="note-length-editor">
          <div><span>FINAL NOTE LENGTH</span><strong>{selectedNote ? isUnresolvedMidiNote(selectedNote) ? "? · 音程未確定" : midiToNoteName(selectedNote.midi) : "MIDIバーを選択"}</strong><small>歌→自動補正の後に、必要な長さを手作業で決める最終層</small></div>
          <label><span>NOTE</span><select aria-label="音長を編集するMIDIノート" value={selectedNoteIndex ?? ""} onChange={(event) => setSelectedNoteIndex(event.target.value === "" ? null : Number(event.target.value))}><option value="">バーをタップ／選択</option>{notes.map((note, index) => <option key={midiNoteKey(note)} value={index}>{index + 1}. {isUnresolvedMidiNote(note) ? "? 音程未確定" : midiToNoteName(note.midi)} · {note.startBeat.toFixed(2)}拍目</option>)}</select></label>
          <div className="note-length-value"><span>LENGTH</span><strong>{selectedNote ? `${selectedNote.durationBeats.toFixed(3)} BEATS` : "—"}</strong><small>{manualEditCount}音を手動調整中</small></div>
          <div className="note-length-actions"><button disabled={!selectedNote} onClick={() => updateSelectedNoteDuration(-selectedNoteStep)}>− {selectedNoteGrid}</button><button disabled={!selectedNote} onClick={() => updateSelectedNoteDuration(selectedNoteStep)}>＋ {selectedNoteGrid}</button><button disabled={!selectedNote} onClick={() => updateSelectedNoteDuration(1)}>＋ 1 BEAT</button><button disabled={!selectedNote} onClick={resetSelectedNoteDuration}>補正結果へ戻す</button></div>
        </div>}
      </section>

      {taskMode === "settings" && <section className="settings-mode-panel" aria-label="設定モード">
        <div><span>SETTINGS MODE</span><strong>{settingsView === "guided" ? "EASY / GUIDED" : "ADVANCED"}</strong><small>{settingsView === "guided" ? "困っている結果を選ぶだけで調整できます" : "数値parameterを直接調整します"}</small></div>
        <div className="settings-mode-tabs"><button className={settingsView === "guided" ? "active" : ""} onClick={() => setSettingsView("guided")}>EASY / GUIDED</button><button className={settingsView === "advanced" ? "active" : ""} onClick={() => setSettingsView("advanced")}>ADVANCED</button></div>
        {settingsView === "guided" && <><div className="guided-setting-actions"><button onClick={() => openConcierge("before")}><strong>録音前チェック</strong><small>BPMとGRIDを普通の言葉で確認</small></button><button onClick={() => openConcierge("after")} disabled={!frames.length}><strong>調整を手伝う</strong><small>録音結果の困りごとから再解析</small></button></div><div className="guided-key-assist"><span>半音の迷いをどうする？</span><button className={conversionMode === "standard" ? "active" : ""} onClick={() => setConversionMode("standard")}>そのまま</button><button className={conversionMode === "key-assist" && keyAssistStrength === "light" ? "active" : ""} onClick={() => { setConversionMode("key-assist"); setKeyAssistStrength("light"); setKeyAssistWeight(18); }}>キーを少し参考</button><button className={conversionMode === "key-assist" && keyAssistStrength !== "light" ? "active" : ""} onClick={() => { setConversionMode("key-assist"); setKeyAssistStrength("strong"); setKeyAssistWeight(50); }}>キーを強めに参考</button><small>{conversionMode === "key-assist" ? assistedKeyLabel : "歌声の判定をそのままMIDIにします"}</small></div></>}
      </section>}
      <section className={`settings-grid ${settingsView === "advanced" ? "" : "guided-hidden"}`} aria-label="詳細録音設定">
        <div className="setting-card bpm-control"><div className="bpm-row"><span>BPM</span><strong>{bpm}</strong><button type="button" className="tap-button" onClick={() => void tapTempo()} disabled={stage === "recording"}>TAP</button></div><input aria-label="BPM" type="range" min="40" max="240" value={bpm} onChange={(event) => { setBpm(Number(event.target.value)); tapTimesRef.current = []; setTapCount(0); }} disabled={stage === "recording"} /><small>{tapCount > 1 ? `${tapCount} taps · 検出 ${bpm} BPM` : "リズムよく2回以上タップ"}</small></div>
        <label className="setting-card sensitivity-control"><span>MIC PICKUP</span><div><strong>{micSensitivity}</strong><em>{sensitivityLabel}</em></div><input aria-label="マイク感度" type="range" min="0" max="100" value={micSensitivity} onChange={(event) => setMicSensitivity(Number(event.target.value))} disabled={stage === "recording"} /><small>{micSensitivity >= 75 ? "小さい鼻歌を拾う・環境音も増える" : "大きめの歌声をきれいに拾う"}</small></label>
        <label className="setting-card detail-control"><span>NOTE DETAIL</span><div><strong>{noteDetail}</strong><em>{detailProfile.label}</em></div><input aria-label="音の細かさ" type="range" min="0" max="100" step="5" value={noteDetail} onChange={(event) => setNoteDetail(Number(event.target.value))} disabled={stage === "recording"} /><small>{noteDetail >= 65 ? "短いF-G-Fと同音FFの再アタックを残す" : "揺れをならして大きなメロディーを取る"}</small></label>
        <label className="setting-card tap-latency-control"><span>RHYTHM TAP OFFSET</span><div><strong>{tapLatencyMs}</strong><em>ms EARLY</em></div><input aria-label="タップ遅延補正" type="range" min="0" max="160" step="5" value={tapLatencyMs} onChange={(event) => setTapLatencyMs(Number(event.target.value))} disabled={stage === "recording"} /><small>歌いながらの反応遅れを前へ補正 · 初期値 {DEFAULT_TAP_LATENCY_MS}ms</small></label>
        <div className="setting-card"><span>GRID</span><div className="segmented grid-options">{(["1/4", "1/8", "1/16", "1/32"] as GridValue[]).map((value) => <button key={value} className={grid === value ? "active" : ""} onClick={() => setGrid(value)} disabled={stage === "recording"}>{value}</button>)}</div><small>短い階段音は1/16〜1/32</small></div>
        <div className="setting-card length-control"><span>MIN NOTE</span><div className="segmented length-options"><button className={!lengthAssist ? "active" : ""} onClick={() => setLengthAssist(false)} disabled={stage === "recording"}>OFF</button>{([{ beats: 0.125, label: "1/32" }, { beats: 0.25, label: "1/16" }, { beats: 0.5, label: "1/8" }, { beats: 1, label: "1/4" }] as const).map((option) => <button key={option.label} className={lengthAssist && minimumNoteBeats === option.beats ? "active" : ""} onClick={() => { setMinimumNoteBeats(option.beats); setLengthAssist(true); }} disabled={stage === "recording"}>{option.label}</button>)}</div><small>短い音だけ保証 · 次の音は越えない</small></div>
        <label className="setting-card select-card"><span>REFERENCE</span><select value={referenceMidi} onChange={(event) => setReferenceMidi(Number(event.target.value))} disabled={stage === "recording"}>{Array.from({ length: 61 }, (_, index) => 36 + index).map((midi) => <option value={midi} key={midi}>{midiToNoteName(midi)}</option>)}</select></label>
        <label className="setting-card select-card"><span>EVERY</span><select value={referenceBars} onChange={(event) => setReferenceBars(Number(event.target.value))} disabled={stage === "recording"}><option value="0">OFF</option><option value="1">1 BAR</option><option value="2">2 BARS</option><option value="4">4 BARS</option><option value="8">8 BARS</option></select></label>
        <div className="setting-card key-assist-control"><span>MIDI CONVERSION</span><div className="segmented"><button className={conversionMode === "standard" ? "active" : ""} onClick={() => setConversionMode("standard")}>STANDARD</button><button className={conversionMode === "key-assist" ? "active" : ""} onClick={() => setConversionMode("key-assist")}>KEY ASSIST</button></div><small>隣の半音へずれる時。検出していない音は作りません。</small></div>
        <div className="setting-card key-assist-control"><span>KEY ASSIST STRENGTH</span><div className="segmented"><button className={keyAssistStrength === "light" ? "active" : ""} onClick={() => { setKeyAssistStrength("light"); setKeyAssistWeight(18); }}>少し</button><button className={keyAssistStrength === "normal" ? "active" : ""} onClick={() => { setKeyAssistStrength("normal"); setKeyAssistWeight(34); }}>標準</button><button className={keyAssistStrength === "strong" ? "active" : ""} onClick={() => { setKeyAssistStrength("strong"); setKeyAssistWeight(50); }}>強め</button></div><input aria-label="Key Assist weight" type="range" min="0" max="75" step="1" value={keyAssistWeight} onChange={(event) => setKeyAssistWeight(Number(event.target.value))} /><small>KEY PRIOR WEIGHT {keyAssistWeight}% · 安定した半音外音と手動編集は保護</small></div>
      </section>
      <section className="input-test-panel">
        <div className="section-heading"><div><span>LIVE INPUT TEST</span><h2>録音せずに声と音程を確認する</h2></div><small>{isInputTesting ? "マイク確認中" : "MIDIには追加しません"}</small></div>
        <div className="input-test-body">
          <div><p>普段の鼻歌を出すと、上のLIVE PITCHとMIC LEVELがリアルタイムで動きます。声が出ているのに音程が「—」なら、その声量からMIC PICKUPを上げられます。</p><strong>{inputTestMessage}</strong><span>PEAK {Math.min(100, Math.round((inputTestPeak / 0.08) * 100))}% · {inputTestFrame?.midiFloat !== null && inputTestFrame?.midiFloat !== undefined ? midiToNoteName(inputTestFrame.midiFloat) : "音程未確定"} · STABILITY {Math.round((inputTestFrame?.confidence ?? 0) * 100)}%</span></div>
          <div className="input-test-actions">{isInputTesting ? <><button className="test-stop" onClick={stopInputTest}>■ テスト停止</button><button onClick={applyInputTestSensitivity} disabled={inputTestPeak <= 0}>この声からPICKUPを調整</button></> : <button onClick={() => void startInputTest()} disabled={isCalibrating || isVoiceMapping || stage === "preparing" || stage === "counting" || stage === "recording"}>● 音程テスト開始</button>}</div>
        </div>
      </section>


      <ViewportDiagnostics />

      <section className="settings-audio-panel">
        <div><span>AUDIO OUTPUT</span><strong>アプリ音量 {outputVolume}%</strong><small>端末メディア音量とは別に、Piano・基準音・Count・Playbackをまとめて調整します</small></div>
        <button onClick={() => void playFourBeatCheck()} disabled={isAudioChecking || stage === "recording"}>{isAudioChecking ? "確認中…" : "4拍を鳴らす"}</button>
        <label className="output-volume-control"><span>OUTPUT VOLUME</span><input aria-label="アプリ出力音量" type="range" min="0" max="100" step="1" value={outputVolume} onChange={(event) => changeOutputVolume(Number(event.target.value))} /></label>
        <code>state {audioDebug?.state ?? "unavailable"} · {audioDebug?.sampleRate ?? "—"} Hz · contexts {audioDebug?.contextCount ?? 0} · warm {audioDebug?.warmedUp ? "yes" : "no"} · play {audioDebug?.playSessionId ?? "—"} · nodes {audioDebug?.activeAudioNodes ?? 0} · voices {audioDebug?.activeVoices ?? 0} · scheduled {audioDebug?.scheduledNotes ?? 0} · output {audioDebug?.outputVolume ?? outputVolume}% · limiter {audioDebug?.limiterReduction?.toFixed(1) ?? "—"} dB{audioDebug?.lastPlaybackError ? " · error " + audioDebug.lastPlaybackError : " · error none"}</code>
        {settingsView === "advanced" && <div className="audio-developer-diagnostics">
          <code>latency base {audioDebug?.baseLatencyMs?.toFixed(1) ?? "—"} ms · output {audioDebug?.outputLatencyMs?.toFixed(1) ?? "—"} ms · route {captureAudioRoute?.device ?? "web/default"} · mode {captureAudioRoute?.audioMode ?? "—"}</code>
          <code>mic permission {microphonePermission} · input {audioDebug?.microphoneState ?? "inactive"} · {audioDebug?.inputSampleRate ?? "—"} Hz · {audioDebug?.inputChannelCount ?? "—"} ch · recorder {audioDebug?.recorderState ?? "unavailable"}</code>
          <code>pitch processing {audioDebug?.lastPitchProcessingMs?.toFixed(2) ?? "—"} ms · detected MIDI {audioDebug?.lastDetectedMidi ?? "—"} · frame {recordedFrame?.frequency?.toFixed(1) ?? "—"} Hz · confidence {recordedFrame ? Math.round(recordedFrame.confidence * 100) : "—"}%</code>
          <code>sources click {audioDebug?.sourceGains.clickRegular.toFixed(2) ?? "0.17"} / piano {audioDebug?.sourceGains.piano.toFixed(2) ?? "0.22"} / edit {audioDebug?.sourceGains.editMelody.toFixed(2) ?? "0.28"} / reference {audioDebug?.sourceGains.reference.toFixed(2) ?? "0.16"}</code>
          <code>scheduler {audioDebug?.metronome.callbackIntervalMs ?? 35} ms · ahead {audioDebug?.metronome.scheduleAheadMs ?? 320} ms · max stall {audioDebug?.metronome.maxCallbackStallMs.toFixed(1) ?? "0.0"} ms · missed {audioDebug?.metronome.missedSchedulingDeadlines ?? 0} · skipped {audioDebug?.metronome.skippedBeats ?? 0} · pending {audioDebug?.metronome.pendingScheduledBeats ?? 0}</code>
          <code>capture {captureSession?.sessionId ?? "—"} · {captureSession?.mode ?? "—"} · {captureSession?.status ?? "—"} · count {captureSession?.countInBeats ?? 4} · pre-roll {captureSession?.preRollBars ?? 0} bars · playback {captureSession?.playbackStartTick ?? 0} · record {captureSession?.recordStartTick ?? 0}–{captureSession?.recordEndTick ?? "STOP"}</code>
          {captureAudioEvents.length > 0 && <ol>{captureAudioEvents.map((event, index) => <li key={`${event.label}-${index}`}><span>{event.label}</span><code>+{Math.round(event.performanceMs - captureAudioEvents[0].performanceMs)} ms · audio {event.audioTime?.toFixed(3) ?? "—"}</code></li>)}</ol>}
        </div>}
      </section>

      <section className="noise-calibration">
        <div className="section-heading"><div><span>ROOM ZERO</span><h2>いまの静寂を基準にする</h2></div><small>マイクや場所を変えたら再測定</small></div>
        <div className="noise-calibration-body">
          <div className="noise-copy"><p>3秒だけ歌わずに測ります。中央値と90%値を環境音のゼロにし、突発音は基準からほぼ除外します。</p>{noiseCalibration ? <><strong>{calibrationQuality} · 静寂 {calibrationPercent}%</strong><span>{noiseCalibration.deviceLabel} · {noiseCalibration.sampleRate} Hz · {noiseCalibration.channelCount} ch</span><span>{calibrationStale ? "MIC PICKUP変更後のため再測定してください" : "次の録音でこの静寂基準を使用します"}{noiseCalibration.hasSpike ? " · 突発音を検出" : ""}</span></> : <span>内蔵・イヤホン・USBなど、実際に使うマイクで測定してください。</span>}</div>
          <button onClick={calibrateEnvironment} disabled={isCalibrating || isVoiceMapping || isInputTesting || stage === "preparing" || stage === "counting" || stage === "recording"}>{isCalibrating ? "3秒測定中…" : noiseCalibration ? "静寂を取り直す" : "静寂を測る"}</button>
        </div>
        {noiseCalibration?.quality === "retake" && <p className="noise-warning">平均的な環境音が高めです。静かな場所へ移るかマイク位置を変えて、もう一度測るのがおすすめです。</p>}
      </section>

      <section className="voice-map-panel">
        <div className="section-heading"><div><span>VOICE MAP</span><h2>自分の声とマイクの拾われ方を覚える</h2></div><small>端末内だけに保存</small></div>
        <div className="voice-map-body">
          <div><p>最初に4拍カウント。その後クリックを止めず、各音を「基準音を4拍聴く→同じ4拍歌う」でつなげます。基準音は鍵盤の長押しと同じ持続音です。最後のロングだけ8拍聴いて8拍歌います。</p><strong>{voiceMapPrompt}</strong>{!activeCalibration && <span>先にROOM ZEROが必要です</span>}</div>
          <div className="voice-map-buttons">{isVoiceMapping ? <><button className="map-stop" onClick={stopVoiceMap}>■ 途中で停止</button>{voiceMapCanSkip && <button onClick={skipVoiceMapNote}>この音は出ない · スキップ</button>}</> : <button onClick={calibrateVoiceMap} disabled={!activeCalibration || isCalibrating || isInputTesting || stage === "preparing" || stage === "counting" || stage === "recording"}>{voiceProfile ? "VOICE MAPを取り直す" : "4拍カウントから声を学習"}</button>}</div>
        </div>
        {isVoiceMapping && <div className="voice-map-progress" role="status"><i /><span>{voiceMapPrompt}</span><div className="voice-beats">{Array.from({ length: voiceMapPhaseBeats }, (_, index) => <b key={index} className={index < voiceMapBeat ? "active" : ""}>{index + 1}</b>)}</div><strong>{voiceMapBeat || "—"} / {voiceMapPhaseBeats}</strong></div>}
        {voiceProfile && (
          <div className="voice-map-result">
            <div className="voice-map-summary"><strong>{voiceProfile.deviceLabel}</strong><span>弱く拾われた音域 {weakVoiceSamples}/{voiceProfile.samples.length} · 推奨MIC PICKUP {voiceProfile.recommendedSensitivity} · ロング検出 {Math.round(voiceProfile.sustainCaptureRatio * 100)}%</span></div>
            <div className="voice-map-notes">{voiceProfile.samples.map((sample) => <div key={sample.targetMidi} className={sample.weak ? "weak" : ""}><strong>{midiToNoteName(sample.targetMidi)}</strong><span>拾音 {Math.min(100, Math.round((sample.medianLevel / 0.06) * 100))}%</span><small>{sample.pitchOffsetCents === null ? "未検出" : `${sample.pitchOffsetCents >= 0 ? "+" : ""}${sample.pitchOffsetCents} cent`}</small></div>)}</div>
            <div className="voice-map-actions"><label><input type="checkbox" checked={voiceMapAssist} onChange={(event) => setVoiceMapAssist(event.target.checked)} disabled={voiceMapStale} /> 上記の拾音・音域別確信度・長音補助を録音へ使用</label><div><button className="apply-pickup" onClick={applyVoiceMapRecommendedSensitivity}>推奨MIC PICKUP {voiceProfile.recommendedSensitivity}を適用</button><button onClick={clearVoiceMap}>この端末のVOICE MAPを消す</button></div></div>
          </div>
        )}
            {mapImpact && <div className="voice-map-impact">
              <article><span>MIC PICKUP</span><strong>{micSensitivity}</strong><small>VOICE MAPは数値を自動変更しません</small></article>
              <article><span>弱音の拾音閾値</span><strong>−{mapImpact.captureReductionPercent}%</strong><small>学習した弱い音域を拾いやすくする</small></article>
              <article><span>音程確信度</span><strong>−{mapImpact.confidenceReductionPercent}%</strong><small>音域別に採用条件を補助</small></article>
              <article><span>長音補助上限</span><strong>＋{mapImpact.sustainExtensionPercent}%</strong><small>次の音を越えず、短音は延ばさない</small></article>
            </div>}
        {voiceMapStale && <p className="noise-warning">マイクまたはMIC PICKUP処理が変わりました。ROOM ZEROの後にVOICE MAPを取り直してください。</p>}
      </section>
      {stage === "result" && (
        <section className="converted-preview">
          <div className="converted-copy"><span>CONVERTED MIDI · {captureSegmentationMode === "rhythm-guided" && rhythmSession ? "RHYTHM GUIDED" : "AUTO"}</span><h2>RAWと音長補助を聴き比べる</h2><p>{rawNotes.length} notes · {grid} grid · {lengthAssist ? `最小 ${minimumNoteLabel}` : "MIN NOTE OFF"} · {voiceMapAssist && activeVoiceProfile ? "VOICE MAP ON" : "VOICE MAP OFF"} · {extendedNoteCount}音を延長</p><small>単発のスタッカートはVOICE MAPで延ばさず、2区間以上続いた音の減衰だけを補助します。次の音を越えず、無かった別音程は作りません。</small></div>
          <div className="converted-actions">
            <div className="segmented output-choice"><button className={!lengthAssist ? "active" : ""} onClick={() => setLengthAssist(false)}>RAWを使う</button><button className={lengthAssist ? "active" : ""} onClick={() => setLengthAssist(true)}>補助後を使う</button></div>
            <div className="preview-pair"><button className="raw-preview" onClick={playRawMidi} disabled={!rawNotes.length || isPlaying}>▶ RAWを聴く</button><button onClick={playConvertedMidi} disabled={!notes.length || isPlaying}><span>▶</span> {lengthAssist ? "補助後を聴く" : "RAWを聴く"}</button></div>
          </div>
        </section>
      )}


      {stage === "result" && (
        <section className="result-panel">
          <div className="section-heading"><div><span>KEY ANALYSIS</span><h2>推定キー候補</h2></div><small>選んだキーは次の録音の2音クリックにも使われます</small></div>
          <div className="key-candidates">
            {keys.length ? keys.map((candidate) => (
              <button key={`${candidate.tonic}-${candidate.mode}`} className={selectedKey === candidate ? "selected" : ""} onClick={() => { setSelectedKey(candidate); setReferenceMidi(60 + candidate.tonicIndex); }}>
                <span>{candidate.tonic} {candidate.mode === "major" ? "Major" : "Minor"}</span><strong>{candidate.confidence}%</strong>
              </button>
            )) : <p>十分な音程を検出できませんでした。MIC PICKUPを上げるか、1/16〜1/32で少し長めに歌ってみてください。</p>}
          </div>
        </section>
      )}

      <section className="project-panel">
        <div className="section-heading"><div><span>PROJECT</span><h2>曲の仮タイトルと書き出し名</h2></div><small>端末内へ自動保存</small></div>
        <div className="project-fields">
          <label><span>PROJECT TITLE</span><input value={projectTitle} maxLength={64} placeholder="夜の曲" onChange={(event) => setProjectTitle(event.target.value)} /></label>
          <label><span>MEMO / TAG（任意）</span><input value={exportMemo} maxLength={32} placeholder="サビ案" onChange={(event) => setExportMemo(event.target.value)} /></label>
        </div>
        <div className="project-save-status" role="status">
          <strong>{projectSaveState === "loading" ? "読込中" : projectSaveState === "saving" ? "保存中…" : projectSaveState === "error" ? "保存エラー" : "保存済み"}</strong>
          <span>{projectStorage === "indexeddb" ? "IndexedDB" : "localStorage退避"} · 次の書き出し v{String(exportCounter).padStart(2, "0")}{projectLastModified ? ` · ${new Date(projectLastModified).toLocaleString("ja-JP")}` : ""}</span>
        </div>
      </section>

      <section className="track-panel">
        <div className="section-heading"><div><span>MULTITRACK ARRANGE · TRACK ≠ CLIP</span><h2>音を重ねる</h2></div><small>TrackとClipは独立して端末内へ保存</small></div>
        {stage === "result" && (
          <div className="track-creator">
            <label><span>TRACK NAME</span><input value={trackName} maxLength={48} onChange={(event) => setTrackName(event.target.value)} /></label>
            <label><span>ROLE / SOUND</span><select value={trackRole} onChange={(event) => setTrackRole(event.target.value as TrackRole)}>{(Object.keys(ROLE_LABELS) as TrackRole[]).map((role) => <option key={role} value={role}>{ROLE_LABELS[role]}</option>)}</select></label>
            <button onClick={addCurrentTrack} disabled={!notes.length}>この変換結果をトラックに追加</button>
          </div>
        )}
        <div className="track-monitor-row">
          <label><input type="checkbox" checked={overdubMonitoringEnabled} onChange={(event) => setOverdubMonitoringEnabled(event.target.checked)} disabled={!tracks.length} /> REC MONITORを有効にして次を録音</label>
          <span>通常再生のPLAY設定とは別です · TrackごとにREC MONを選択 · イヤホン推奨</span>
        </div>
        {tracks.length ? (
          <MultiTrackArrange
            tracks={tracks}
            clips={clips}
            ppq={currentPpq}
            roleLabels={ROLE_LABELS}
            roleColors={ROLE_COLORS}
            selectedTrackId={selectedTrackId}
            selectedClipId={selectedClipId}
            isPlaying={isPlaying}
            isExporting={isExporting}
            onSelectTrack={setSelectedTrackId}
            onSelectClip={setSelectedClipId}
            onChangeTrack={updateTrack}
            onPlayTrack={playProjectTrack}
            onExportTrack={exportTrack}
          />
        ) : <p className="track-empty">変換結果へ名前と役割を付けて追加すると、次の録音でそのMIDIを聴きながら重ねられます。</p>}
        {tracks.length > 0 && (
          <div className="track-master-actions"><button onClick={() => void playTrackStack()} disabled={!activeTracks.length || isPlaying}>通常再生modeでまとめて聴く</button><button className="export-stack" onClick={exportTrackStack} disabled={!exportMidiTracks.length || isExporting}>全MIDI表現をType 1出力</button></div>
        )}
      </section>

      <section className="keyboard-panel">
        <div className="section-heading"><div><span>SCALE KEYBOARD / CHORD PAD</span><h2>{keyLabel}</h2></div><label className="lock-switch"><input type="checkbox" checked={scaleLock} onChange={(event) => setScaleLock(event.target.checked)} /><span /> SCALE LOCK</label></div>
        {selectedKey && (
          <>
            <div className="chord-toolbar"><span>基本4は頻出コードの近道です。理論上はキー内に7つあります。</span><div><label className="seventh-switch"><input type="checkbox" checked={seventh} onChange={(event) => setSeventh(event.target.checked)} /> 7TH</label><label className="seventh-switch"><input type="checkbox" checked={showAllChords} onChange={(event) => setShowAllChords(event.target.checked)} /> 全7コード</label></div></div>
            <div className="chord-pads">{chordPads.map(({ degree, label }) => {
              const chord = buildChord(selectedKey.tonicIndex, selectedKey.mode, degree, seventh, 48);
              return <button key={label} {...keyHoldProps(`chord-${label}`, chord)}><strong>{label}{seventh ? "7" : ""}</strong><span>{chord.map(midiToNoteName).join(" · ")}</span></button>;
            })}</div>
          </>
        )}
        <div className="keyboard" aria-label="C3からC6の3オクターブ鍵盤">
          {KEYBOARD_NOTES.filter((midi) => !BLACK.has(midi % 12)).map((midi) => {
            const pitchClass = midi % 12;
            const inScale = !selectedKey || scale.has(pitchClass);
            const playable = canPlayMidi(midi);
            return <button key={midi} className={`white-key ${inScale ? "in-scale" : "out-scale"}`} {...(playable ? keyHoldProps(`key-${midi}`, [midi]) : {})} aria-disabled={!playable} aria-label={`${midiToNoteName(midi)}、押している間鳴る`}><span>{pitchClass === 0 ? midiToNoteName(midi) : NOTE_NAMES[pitchClass]}</span></button>;
          })}
          {KEYBOARD_NOTES.filter((midi) => BLACK.has(midi % 12)).map((midi) => {
            const whiteBefore = KEYBOARD_NOTES.filter((note) => note < midi && !BLACK.has(note % 12)).length;
            const inScale = !selectedKey || scale.has(midi % 12);
            const playable = canPlayMidi(midi);
            return <button key={midi} style={{ left: `calc(${whiteBefore} * var(--white-key-width) - var(--black-key-width) / 2)` }} className={`black-key ${inScale ? "in-scale" : "out-scale"}`} {...(playable ? keyHoldProps(`key-${midi}`, [midi]) : {})} aria-disabled={!playable} aria-label={`${midiToNoteName(midi)}、押している間鳴る`} />;
          })}
        </div>
      </section>

        <section className="arrangement-panel">
          <div className="section-heading"><div><span>AUTO CHORD GUIDE</span><h2>理論を知らなくてもコード候補を置く</h2></div><small>メロディーは変更しません</small></div>
          <div className="arrangement-controls">
            <div><span>STYLE</span><div className="segmented wide">{(["off", "chords", "arpeggio"] as AccompanimentMode[]).map((mode) => <button key={mode} className={accompanimentMode === mode ? "active" : ""} onClick={() => { setAccompanimentMode(mode); if (mode === "off") setExportMix(false); }}>{mode === "off" ? "OFF" : mode === "chords" ? "CHORDS" : "ARPEGGIO"}</button>)}</div></div>
            <div><span>MIDI OUTPUT</span><div className="segmented wide"><button className={!exportMix ? "active" : ""} onClick={() => setExportMix(false)}>MELODY</button><button className={exportMix ? "active" : ""} onClick={() => setExportMix(true)} disabled={!accompaniment.length}>＋ ACCOMP</button></div></div>
          </div>
          <div className="harmony-route"><span>CHORD ROUTE</span><div className="segmented wide">{([{ value: "melody-fit", label: "なじむ" }, { value: "6451", label: "6-4-5-1" }, { value: "smooth", label: "滑らか" }, { value: "dramatic", label: "5→1 DRAMA" }] as const).map((option) => <button key={option.value} className={harmonyRoute === option.value ? "active" : ""} onClick={() => changeHarmonyRoute(option.value)}>{option.label}</button>)}</div><div className="harmony-switches"><label><input type="checkbox" checked={seventh} onChange={(event) => setSeventh(event.target.checked)} /> 7thを加える</label>{harmonyRoute === "smooth" && <label><input type="checkbox" checked={bridgeChords} onChange={(event) => setBridgeChords(event.target.checked)} /> Bar間にslashコード</label>}</div></div>
          <div className="chord-timeline">{chordPlan.map((suggestion) => (
            <article key={suggestion.bar} className={suggestion.warning ? "warning" : ""}>
              <span>BAR {suggestion.bar + 1}</span><strong>{suggestion.label}{effectiveSeventh ? "7" : ""}</strong><small>{suggestion.reason}{suggestion.warning ? " · メロディーと半音衝突注意" : " · 理論チェック済み候補"}</small><button onClick={() => cycleChord(suggestion.bar)}>安全候補を変更</button>
            </article>
          ))}</div>
          {chordTransitions.length > 0 && <div className="bridge-list"><span>BAR間</span>{chordTransitions.map((transition) => <strong key={transition.atBeat}>{transition.label} · {transition.atBeat.toFixed(1)} beat</strong>)}</div>}
          <p>{harmonyRoute === "6451" ? "定番進行を先に置きます" : harmonyRoute === "smooth" ? "共通音を優先し、ONなら境界の最後半拍に前コード／次ルートを置きます" : harmonyRoute === "dramatic" ? "5→1の解決と7thを強めます" : "小節内で長く鳴る音を含み、半音衝突が少ないコードを優先します"} · キー内候補でも曲調に合う保証はないため、ARRANGE VIEWで聴いて決めます · 伴奏 {accompaniment.length} notes</p>
          <button className="arrangement-preview" onClick={playSelectedMidi} disabled={!selectedMidi.length || isPlaying}>選択中の伴奏込みMIDIを聴く</button>
        </section>

      <section className="action-dock">
        {isPlaying && <button className="stop-action" onClick={stopPlayback}><span>STOP</span>■ 再生を止める</button>}
        <button onClick={playVoice} disabled={!voiceUrl || isPlaying}><span>VOICE</span>録った音声</button>
        <button onClick={playConvertedMidi} disabled={!notes.length || isPlaying}><span>CONVERTED</span>変換MIDIを聴く</button>
        <button className="export-action" onClick={exportMidi} disabled={!notes.length || isExporting}><span>EXPORT CURRENT</span>{isExporting ? "保存・共有中…" : "現在の.mid"}</button>
        {exportNotice && <output className="export-notice" aria-live="polite">{exportNotice}</output>}
      </section>

      {stage === "result" && frames.length > 0 && (
        <details className="diagnostic-panel">
          <summary>精度診断ログ <span>検出 {frames.filter((frame) => frame.frequency !== null).length}/{frames.length} frames · MIDI {notes.length} notes · SLOTS {rhythmSlots.length}</span></summary>
          <div className="diagnostic-table"><span>START</span><span>NOTE</span><span>LENGTH</span>{notes.map((note, index) => <div className="diagnostic-row" key={`${note.startBeat}-${index}`}><span>{note.startBeat.toFixed(2)} beat</span><strong>{isUnresolvedMidiNote(note) ? "? · UNRESOLVED" : midiToNoteName(note.midi)}</strong><span>{note.durationBeats.toFixed(2)} beat · {note.source ?? "AUTO"}</span></div>)}</div>
        </details>
      )}
      <footer><span>重ね録りはイヤホン使用でガイド音混入を減らせます</span><span>音声とトラックはアップロードされません</span></footer>
    </main>
  );
}
